import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Select from 'react-select';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock,
  GripVertical,
  KanbanSquare,
  ListChecks,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import api from '../../apiPm';
import {
  emptyProjectDetail,
  getCurrentUserId,
  getHealthTone,
  getStageTone,
  getSafeApiErrorMessage,
  normalizeProjectDetail,
  sampleDetail,
  shouldUseSampleData,
  withTimeout,
} from '../PmShared/projectUtils';
import './PmProjectDetail.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const KANBAN_COLS = [
  { key: 'to_do', label: 'To Do', tone: 'secondary' },
  { key: 'in_progress', label: 'In Progress', tone: 'primary' },
  { key: 'in_review', label: 'In Review', tone: 'warning' },
  { key: 'blocked', label: 'Blocked', tone: 'danger' },
  { key: 'done', label: 'Done', tone: 'success' },
];

// Leaf statuses the backend always forces to a fixed progress value,
// ignoring any manual input (see leafProgressForStatus in the Go PM
// service) — mirrored here so the Task Detail drawer shows them read-only
// instead of letting the user edit a value that silently reverts on save.
const LOCKED_LEAF_PROGRESS = { to_do: 0, done: 100 };

const PRIO_LABEL = { 1: 'Low', 2: 'Normal', 3: 'High', 4: 'Critical' };
const PRIO_TONE = { 1: 'secondary', 2: 'info', 3: 'warning', 4: 'danger' };
const USE_DUMMY_TASK_DATA = false;

const tabs = [
  { key: 'overview', label: 'Overview', icon: ListChecks },
  { key: 'tasks', label: 'Tasks', icon: ListChecks },
  { key: 'kanban', label: 'Kanban', icon: KanbanSquare },
  { key: 'gantt', label: 'Gantt', icon: CalendarDays },
  { key: 'team', label: 'Team', icon: Users },
  { key: 'activity', label: 'Activity', icon: Activity },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (v) => {
  const d = parseDate(v);
  return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-';
};

const ACTIVITY_TONE = {
  created: 'success',
  subtask_created: 'success',
  updated: 'primary',
  deleted: 'danger',
};
const activityTone = (action) => ACTIVITY_TONE[action] || 'primary';
const ActivityActionIcon = ({ action, size = 12 }) => {
  if (action === 'deleted') return <Trash2 size={size} />;
  if (action === 'created' || action === 'subtask_created') return <Plus size={size} />;
  return <Pencil size={size} />;
};

// HH:mm:ss — the running Work Timer display while a session is active.
const formatDurationClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const getRunningTimerStartedAt = (log) => {
  const raw = log?.started_at || log?.startedAt;
  if (!raw) return null;
  const startedAt = new Date(raw).getTime();
  return Number.isFinite(startedAt) ? startedAt : null;
};

// "1h 25m" / "12m" / "45s" — closed-session durations in the Work Logs panel.
const formatDurationShort = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  if (s < 60) return `${s}s`;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  return hours === 0 ? `${minutes}m` : `${hours}h ${minutes}m`;
};

const fmtActivityTime = (v) => {
  const d = parseDate(v);
  if (!d) return '-';
  const diffMs = Date.now() - d.getTime();
  if (diffMs >= 0 && diffMs < 60 * 1000) return 'just now';
  if (diffMs >= 0 && diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const toInputDate = (v) => {
  const d = parseDate(v);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const localDateTimeToIso = (date, time) => {
  const value = new Date(`${date}T${time}:00`);
  return Number.isNaN(value.getTime()) ? '' : value.toISOString();
};

const statusTone = (keyName) => {
  switch (keyName) {
    case 'done': return 'success';
    case 'in_progress': return 'primary';
    case 'in_review': return 'warning';
    case 'blocked': return 'danger';
    default: return 'secondary';
  }
};

const statusLabel = (task) =>
  task.status_title ||
  KANBAN_COLS.find(c => c.key === (task.status_key || task.status))?.label ||
  task.status || '-';

const prioLabel = (id) => PRIO_LABEL[id] || 'Normal';
const prioTone = (id) => PRIO_TONE[id] || 'info';
const taskKey = (t) => t.status_key || t.status || 'to_do';
const taskAssigneeIds = (task) => {
  const ids = String(task?.collaborators || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  if (task?.assigned_to) ids.unshift(String(task.assigned_to));
  return Array.from(new Set(ids));
};
// CRM-project-linked tasks (pm_project_tasks) use application-generated UUID
// ids, not the legacy numeric pm_tasks ids — Number(uuid) is always NaN, so
// every id must be compared as a string. '' is the "no parent" sentinel.
const idKey = (v) => (v === null || v === undefined || v === '' ? '' : String(v));
const getParentId = (task) => idKey(task.parent_task_id ?? task.parentTaskId);
const clampPct = (value) => Math.min(100, Math.max(0, Number(value) || 0));
const getPlanStart = (task) => task.plan_start_date || task.planStartDate || task.start_date || task.startDate;
const getPlanFinish = (task) => task.plan_finish_date || task.planFinishDate || task.deadline || task.dueDate || task.finishDate;
const getActualStart = (task) => task.actual_start_date || task.actualStartDate;
const getActualFinish = (task) => task.actual_finish_date || task.actualFinishDate;
const getProgressPct = (task) => clampPct(task.progress_pct ?? task.progressPct ?? task.percentage ?? task.progress);

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const diffDays = (a, b) => Math.round((getStartOfDay(a) - getStartOfDay(b)) / 86400000);
const getPlannedPct = (task, referenceDate = new Date()) => {
  const start = parseDate(getPlanStart(task));
  const finish = parseDate(getPlanFinish(task));
  if (!start && !finish) return 0;
  if (!start) return referenceDate >= finish ? 100 : 0;
  if (!finish) return referenceDate >= start ? 100 : 0;
  if (referenceDate <= start) return 0;
  if (referenceDate >= finish) return 100;
  const duration = Math.max(1, finish - start);
  return clampPct(Math.round(((referenceDate - start) / duration) * 100));
};

const getScheduleVarianceDays = (task, referenceDate = new Date()) => {
  const planFinish = parseDate(getPlanFinish(task));
  if (!planFinish) return 0;
  const actualFinish = parseDate(getActualFinish(task));
  if (actualFinish) return diffDays(actualFinish, planFinish);
  if (getProgressPct(task) >= 100) return 0;
  return Math.max(0, diffDays(referenceDate, planFinish));
};


// ─── Shared sub-components ────────────────────────────────────────────────────

const TONE_COLOR = {
  primary: { color: '#1f8f98', bg: 'rgba(31,143,152,0.10)' },
  success: { color: '#00a679', bg: 'rgba(0,166,121,0.10)' },
  warning: { color: '#c87b00', bg: 'rgba(245,159,0,0.12)' },
  info:    { color: '#1672b9', bg: 'rgba(22,114,185,0.10)' },
  purple:  { color: '#6f42c1', bg: 'rgba(111,66,193,0.10)' },
};

const SummaryCard = ({ icon: Icon, label, value, tone, isUnavailable = false, onClick, isActive = false }) => {
  const { color, bg } = TONE_COLOR[tone] || TONE_COLOR.primary;
  const clickable = typeof onClick === 'function' && !isUnavailable;
  return (
    <div className="col-6 col-xl">
      <div
        className={`card h-100 border-0 shadow-sm ${clickable ? 'pm-summary-card-clickable' : ''} ${isActive ? 'pm-summary-card-active' : ''}`}
        style={{ borderTop: `3px solid ${color}` }}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? onClick : undefined}
        onKeyDown={clickable ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        } : undefined}
      >
        <div className="card-body d-flex align-items-center gap-3 py-3">
          <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 46, height: 46, background: bg }}>
            <Icon size={20} color={color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="text-muted text-truncate" style={{ fontSize: '0.73rem' }}>{label}</div>
            <div className="fw-bold mt-1" style={{ fontSize: '1.4rem', lineHeight: 1, color }}>
              {isUnavailable ? '-' : value}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailSkeleton = () => (
  <div className="pm-detail-skeleton">
    <div className="card mb-3">
      <div className="card-body">
        <div className="placeholder-glow">
          <span className="placeholder col-3 mb-3 d-block" />
          <span className="placeholder col-8 d-block" />
        </div>
      </div>
    </div>
    <div className="row g-3 mb-3">
      {[1, 2, 3, 4, 5].map(i => (
        <div className="col-6 col-xl" key={i}>
          <div className="card">
            <div className="card-body placeholder-glow">
              <span className="placeholder col-8 mb-2 d-block" />
              <span className="placeholder col-5 d-block" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="pm-detail-empty-state text-muted">
    <Icon size={42} className="mb-2 opacity-25" />
    <div className="fw-semibold">{title}</div>
    <div className="pm-detail-helper">{description}</div>
  </div>
);

// ─── Tasks Tab ────────────────────────────────────────────────────────────────

function TaskRow({ task, childrenByParent, depth, isManualSort, onDelete, onAddChild, onTaskClick }) {
  const sk = taskKey(task);
  const taskId = idKey(task.id);
  const children = childrenByParent.get(taskId) || [];
  const hasChildren = children.length > 0;
  const isChild = depth > 0;
  const progress = getProgressPct(task);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: taskId, disabled: !isManualSort });
  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const dragHandleProps = isManualSort ? { ...attributes, ...listeners } : {};

  return (
    <>
      <tr
        ref={setNodeRef}
        style={rowStyle}
        className={`pm-task-clickable-row ${isChild ? 'pm-task-child-row' : 'pm-task-parent-row'} ${isDragging ? 'pm-task-row-dragging' : ''}`}
        onClick={() => onTaskClick(task)}
      >
        <td style={{ paddingLeft: `${depth * 1.4 + 0.75}rem` }}>
          <div className="d-flex align-items-center gap-1">
            {isManualSort ? (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-ghost-secondary pm-task-drag-handle"
                onClick={(event) => event.stopPropagation()}
                title="Drag to reorder"
                {...dragHandleProps}
              >
                <GripVertical size={14} />
              </button>
            ) : (
              <span className="pm-task-tree-spacer" />
            )}
            {isChild && <span className="pm-task-tree-branch" />}
            <span className="fw-semibold pm-task-title">{task.title}</span>
            <span className={`badge ${isChild ? 'bg-cyan-lt' : 'bg-indigo-lt'} pm-task-type-badge`}>
              {isChild ? 'Child' : 'Parent'}
            </span>
            {hasChildren && (
              <span className="badge bg-secondary-lt pm-task-type-badge">
                {children.length} subtask{children.length !== 1 ? 's' : ''}
              </span>
            )}
            {task.labels && (
              <span className="badge bg-secondary-lt pm-task-type-badge">{task.labels}</span>
            )}
          </div>
        </td>
        <td><span className={`badge bg-${prioTone(task.priority_id)}-lt`}>{prioLabel(task.priority_id)}</span></td>
        <td><span className={`badge bg-${statusTone(sk)}-lt`}>{statusLabel(task)}</span></td>
        <td className="text-muted pm-detail-helper">{task.assignee_name || '-'}</td>
        <td className="text-muted pm-detail-helper">{fmtDate(getPlanStart(task))}</td>
        <td className="text-muted pm-detail-helper">{fmtDate(getPlanFinish(task))}</td>
        <td className="text-muted pm-detail-helper">{fmtDate(getActualStart(task))}</td>
        <td className="text-muted pm-detail-helper">{fmtDate(getActualFinish(task))}</td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <div className="progress" style={{ height: 4, width: 56, flexShrink: 0 }}>
              <div className="progress-bar bg-primary" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-muted pm-detail-helper">{progress}%</span>
          </div>
        </td>
        <td>
          <div className="btn-list justify-content-end flex-nowrap">
            <button
              type="button"
              className="btn btn-icon btn-sm btn-ghost-primary"
              onClick={(event) => { event.stopPropagation(); onAddChild(task.id); }}
              title="Add subtask"
            >
              <Plus size={14} />
            </button>
            <button className="btn btn-icon btn-sm btn-ghost-danger" onClick={(event) => { event.stopPropagation(); onDelete(task.id); }} title="Delete task">
              <Trash2 size={14} />
            </button>
          </div>
        </td>
      </tr>
      {children.map(child => (
        <TaskRow
          key={child.id}
          task={child}
          childrenByParent={childrenByParent}
          depth={depth + 1}
          onDelete={onDelete}
          onAddChild={onAddChild}
          onTaskClick={onTaskClick}
          isManualSort={isManualSort}
        />
      ))}
    </>
  );
}

function TasksTab({ tasks, onAddTask, onDeleteTask, onTaskClick, onReorderTasks }) {
  const [parentSort, setParentSort] = useState('manual');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const isManualSort = parentSort === 'manual';

  const byText = useCallback((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'id', { sensitivity: 'base' }), []);
  const byDate = useCallback((getter) => (a, b) => {
    const aTime = parseDate(getter(a))?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bTime = parseDate(getter(b))?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aTime - bTime || byText(a, b);
  }, [byText]);
  const byManualOrder = useCallback((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0) || byDate(getPlanStart)(a, b), [byDate]);

  const { rootTasks, childrenByParent, childTaskCount } = useMemo(() => {
    const taskIds = new Set(tasks.map((task) => idKey(task.id)));
    const childMap = new Map();
    const roots = [];
    let childCount = 0;

    tasks.forEach((task) => {
      const parentId = getParentId(task);
      if (parentId && taskIds.has(parentId)) {
        childCount += 1;
        const existing = childMap.get(parentId) || [];
        existing.push(task);
        childMap.set(parentId, existing);
      } else {
        roots.push(task);
      }
    });
    childMap.forEach((children) => children.sort(byManualOrder));

    return { rootTasks: roots, childrenByParent: childMap, childTaskCount: childCount };
  }, [byManualOrder, tasks]);

  const sortedRootTasks = useMemo(() => {
    const sorted = [...rootTasks];
    switch (parentSort) {
      case 'title':
        return sorted.sort(byText);
      case 'plan_start':
        return sorted.sort(byDate(getPlanStart));
      case 'plan_finish':
        return sorted.sort(byDate(getPlanFinish));
      case 'priority':
        return sorted.sort((a, b) => Number(b.priority_id || 0) - Number(a.priority_id || 0) || byText(a, b));
      case 'status':
        return sorted.sort((a, b) => statusLabel(a).localeCompare(statusLabel(b), 'id', { sensitivity: 'base' }) || byText(a, b));
      default:
        return sorted.sort(byManualOrder);
    }
  }, [byDate, byManualOrder, byText, parentSort, rootTasks]);

  const { visibleTaskIds, siblingsByTaskId } = useMemo(() => {
    const visibleIds = [];
    const siblingMap = new Map();
    const registerSiblings = (items) => {
      const ids = items.map((item) => idKey(item.id));
      ids.forEach((itemId) => siblingMap.set(itemId, ids));
    };
    const walk = (task) => {
      const taskId = idKey(task.id);
      visibleIds.push(taskId);
      const children = childrenByParent.get(taskId) || [];
      if (children.length > 0) {
        registerSiblings(children);
        children.forEach(walk);
      }
    };

    registerSiblings(sortedRootTasks);
    sortedRootTasks.forEach(walk);
    return { visibleTaskIds: visibleIds, siblingsByTaskId: siblingMap };
  }, [childrenByParent, sortedRootTasks]);

  const handleHierarchyDragEnd = useCallback(({ active, over }) => {
    if (!isManualSort || !active || !over) return;
    const activeId = idKey(active.id);
    const overId = idKey(over.id);
    if (!activeId || !overId || activeId === overId) return;

    const activeSiblings = siblingsByTaskId.get(activeId);
    const overSiblings = siblingsByTaskId.get(overId);
    if (!activeSiblings || activeSiblings !== overSiblings) return;

    const oldIndex = activeSiblings.indexOf(activeId);
    const newIndex = activeSiblings.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const task = tasks.find((item) => idKey(item.id) === activeId);
    if (!task) return;
    const orderedIds = arrayMove(activeSiblings, oldIndex, newIndex);
    onReorderTasks(getParentId(task), orderedIds);
  }, [isManualSort, onReorderTasks, siblingsByTaskId, tasks]);

  const taskTable = (
    <div className="table-responsive">
      <table className="table table-vcenter table-hover pm-task-table">
        <thead>
          <tr>
            <th>Task</th>
            <th style={{ width: 90 }}>Priority</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 140 }}>Assignee</th>
            <th style={{ width: 110 }}>Plan Start</th>
            <th style={{ width: 110 }}>Plan Finish</th>
            <th style={{ width: 110 }}>Actual Start</th>
            <th style={{ width: 110 }}>Actual Finish</th>
            <th style={{ width: 120 }}>Progress</th>
            <th className="text-end" style={{ width: 88 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedRootTasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              childrenByParent={childrenByParent}
              depth={0}
              onDelete={onDeleteTask}
              onAddChild={onAddTask}
              onTaskClick={onTaskClick}
              isManualSort={isManualSort}
            />
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div className="pm-task-toolbar">
        <div>
          <div className="fw-semibold">Task Hierarchy</div>
          <div className="text-muted pm-detail-helper">
            {rootTasks.length} parent task{rootTasks.length !== 1 ? 's' : ''} · {childTaskCount} child task{childTaskCount !== 1 ? 's' : ''}
            {isManualSort ? ' · drag handles to reorder' : ''}
          </div>
        </div>
        <div className="btn-list">
          <label className="pm-task-sort-control">
            <span>Sort parent</span>
            <select className="form-select form-select-sm" value={parentSort} onChange={(event) => setParentSort(event.target.value)}>
              <option value="manual">Manual order</option>
              <option value="plan_start">Plan start</option>
              <option value="plan_finish">Plan finish</option>
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="title">Title A-Z</option>
            </select>
          </label>
          <button className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 pm-task-add-btn" onClick={() => onAddTask()}>
            <Plus size={14} /> Task
          </button>
        </div>
      </div>
      {tasks.length === 0 ? (
        <EmptyTabState icon={ListChecks} title="No tasks yet" description="Create the first parent task, then add child tasks below it." />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleHierarchyDragEnd}>
          <SortableContext items={visibleTaskIds} strategy={verticalListSortingStrategy}>
            {taskTable}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ─── Kanban (DnD) ─────────────────────────────────────────────────────────────

function DraggableCard({ task, childCount = 0, onCardClick }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(task.id),
  });
  const progress = getProgressPct(task);
  const style = {
    cursor: isDragging ? 'grabbing' : 'grab',
    ...(transform
      ? { transform: `translate(${transform.x}px,${transform.y}px)`, zIndex: 999, opacity: 0.88 }
      : {}),
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="pm-kanban-card"
      onClick={!isDragging ? () => onCardClick(task) : undefined}
    >
      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
        <div className="fw-semibold" style={{ fontSize: '0.875rem', lineHeight: 1.3 }}>{task.title}</div>
        <div className="d-flex flex-column align-items-end gap-1">
          <span className={`badge bg-${prioTone(task.priority_id)}-lt flex-shrink-0`}>{prioLabel(task.priority_id)}</span>
          {childCount > 0 && <span className="badge bg-secondary-lt flex-shrink-0">{childCount} subtasks</span>}
        </div>
      </div>
      {task.description && (
        <div
          className="text-muted pm-detail-helper mb-2"
          style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {task.description}
        </div>
      )}
      <div className="d-flex align-items-center justify-content-between text-muted pm-detail-helper mb-2">
        <span className="d-flex align-items-center gap-1"><Users size={12} />{task.assignee_name || 'Unassigned'}</span>
        <span className="d-flex align-items-center gap-1"><Clock size={12} />{fmtDate(getPlanFinish(task))}</span>
      </div>
      <div className="progress pm-kanban-progress">
        <div className="progress-bar bg-primary" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function DroppableColumn({ colKey, label, tone, tasks, childCountByTask, onCardClick }) {
  const { isOver, setNodeRef } = useDroppable({ id: colKey });
  return (
    <div
      className="pm-kanban-column"
      ref={setNodeRef}
      style={{ background: isOver ? 'rgba(31,143,152,0.06)' : undefined, transition: 'background 0.15s' }}
    >
      <div className="pm-kanban-header">
        <span>{label}</span>
        <span className={`badge bg-${tone}-lt`}>{tasks.length}</span>
      </div>
      <div className="pm-kanban-stack">
        {tasks.length === 0
          ? <div className="pm-kanban-empty">Drop here</div>
          : tasks.map(task => <DraggableCard key={task.id} task={task} childCount={childCountByTask.get(idKey(task.id)) || 0} onCardClick={onCardClick} />)
        }
      </div>
    </div>
  );
}

function KanbanTab({ tasks, onStatusChange, onAddTask, onCardClick }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { tasksByCol, childCountByTask } = useMemo(() => {
    const result = {};
    const childMap = new Map();
    KANBAN_COLS.forEach(c => { result[c.key] = []; });
    tasks.forEach(t => {
      const parentId = getParentId(t);
      if (parentId) childMap.set(parentId, (childMap.get(parentId) || 0) + 1);
      const k = taskKey(t);
      if (result[k]) result[k].push(t);
      else result['to_do'].push(t);
    });
    return { tasksByCol: result, childCountByTask: childMap };
  }, [tasks]);

  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || !active) return;
    const activeId = idKey(active.id);
    const newKey = String(over.id);
    const task = tasks.find(t => idKey(t.id) === activeId);
    if (!task || taskKey(task) === newKey) return;
    onStatusChange(task.id, newKey);
  }, [tasks, onStatusChange]);

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => onAddTask()}>
          <Plus size={14} /> Add Task
        </button>
      </div>
      {tasks.length === 0 ? (
        <EmptyTabState icon={KanbanSquare} title="No tasks yet" description="Tasks will appear in the Kanban board once created." />
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="pm-kanban-board">
            {KANBAN_COLS.map(col => (
              <DroppableColumn
                key={col.key}
                colKey={col.key}
                label={col.label}
                tone={col.tone}
                tasks={tasksByCol[col.key] || []}
                childCountByTask={childCountByTask}
                onCardClick={onCardClick}
              />
            ))}
          </div>
        </DndContext>
      )}
    </div>
  );
}

// ─── Gantt Tab (project-level) ─────────────────────────────────────────────────

const GANTT_DAY_WIDTH = 30; // px/day — board width scales with range so ticks never crowd, even zoomed in

// Position/width (%) of a bar inside the timeline, given any start/end value
// pair (project dates, plan dates, or actual dates all go through this).
// A missing end collapses to a 1-day sliver at start rather than disappearing.
const buildBar = (timeline, startVal, endVal) => {
  if (!timeline) return null;
  const start = parseDate(startVal);
  if (!start) return null;
  const end = parseDate(endVal) || start;
  const startOffset = Math.max(0, diffDays(start, timeline.start));
  const span = Math.max(1, diffDays(end, start) + 1);
  const left = (startOffset / timeline.totalDays) * 100;
  return { left, width: Math.max(0.6, Math.min(100 - left, (span / timeline.totalDays) * 100)) };
};

// A bar wide enough to hold "100%" gets the label centered inside it (white
// text, matches the existing status-tone bars); anything narrower gets the
// label placed just past the bar's end instead, so short tasks stay readable.
const GanttBarLabel = ({ bar, label }) =>
  bar.width >= 6
    ? <span className="pm-gantt-project-bar-pct">{label}</span>
    : <span className="pm-gantt-project-bar-pct-outside">{label}</span>;

function GanttTab({ project, tasks }) {
  const today = useMemo(() => new Date(), []);

  // Range: project start/end is the baseline; any plan/actual task date
  // outside that range expands it (so out-of-range tasks stay visible), and
  // if the project itself has no dates at all the task dates become the
  // baseline instead (the documented fallback).
  const timeline = useMemo(() => {
    let min = parseDate(project?.projectStartDate);
    let max = parseDate(project?.projectEndDate);
    tasks.forEach((t) => {
      [getPlanStart(t), getPlanFinish(t), getActualStart(t), getActualFinish(t)].forEach((v) => {
        const d = parseDate(v);
        if (!d) return;
        if (!min || d < min) min = d;
        if (!max || d > max) max = d;
      });
    });
    if (!min && !max) return null;
    if (!min) min = max;
    if (!max) max = min;
    if (max < min) { const swap = min; min = max; max = swap; }

    const start = getStartOfDay(min);
    start.setDate(start.getDate() - 3);
    const end = getStartOfDay(max);
    end.setDate(end.getDate() + 3);
    return { start, totalDays: Math.max(14, diffDays(end, start)) };
  }, [project, tasks]);

  const months = useMemo(() => {
    if (!timeline) return [];
    const { start, totalDays } = timeline;
    const result = [];
    const rangeEnd = new Date(start.getTime() + totalDays * 86400000);
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= rangeEnd) {
      const startDay = Math.max(0, diffDays(cur, start));
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const endDay = Math.min(totalDays, diffDays(next, start));
      result.push({
        label: cur.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        left: (startDay / totalDays) * 100,
        width: ((endDay - startDay) / totalDays) * 100,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return result;
  }, [timeline]);

  // Weekly date ticks — spacing is fixed in days, but since the track's
  // pixel width scales 1:1 with totalDays (GANTT_DAY_WIDTH), the on-screen
  // gap between ticks is always ~7 * GANTT_DAY_WIDTH px, so labels never
  // overlap regardless of how long the project runs; long ranges just widen
  // the board and scroll instead of squeezing.
  const dateTicks = useMemo(() => {
    if (!timeline) return [];
    const { start, totalDays } = timeline;
    const ticks = [];
    for (let day = 0; day <= totalDays; day += 7) {
      const d = new Date(start.getTime() + day * 86400000);
      ticks.push({ label: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), left: (day / totalDays) * 100 });
    }
    return ticks;
  }, [timeline]);

  const trackWidthPx = timeline ? Math.max(760, timeline.totalDays * GANTT_DAY_WIDTH) : 760;
  const trackStyle = { width: trackWidthPx, '--gantt-week-px': `${GANTT_DAY_WIDTH * 7}px` };

  // One row per parent, followed immediately by its own active children —
  // matches the same direct-parent-then-children grouping used by the Tasks
  // tab and Kanban, so hierarchy reads the same way across tabs.
  const rows = useMemo(() => {
    const parents = tasks.filter((t) => getParentId(t) === '');
    const childrenByParent = new Map();
    tasks.forEach((t) => {
      const pid = getParentId(t);
      if (!pid) return;
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid).push(t);
    });
    const result = [];
    parents.forEach((parent) => {
      result.push({ kind: 'parent', task: parent });
      (childrenByParent.get(idKey(parent.id)) || []).forEach((child) => result.push({ kind: 'child', task: child }));
    });
    return result;
  }, [tasks]);

  if (!timeline) {
    return <EmptyTabState icon={CalendarDays} title="No dates yet" description="Set project or task dates to see the Gantt chart." />;
  }

  const projectBar = buildBar(timeline, project?.projectStartDate, project?.projectEndDate);
  const projectProgress = clampPct(project?.progress);

  return (
    <div>
      <div className="pm-gantt-toolbar mb-3">
        <div>
          <div className="fw-semibold">Project Gantt</div>
          <div className="text-muted pm-detail-helper">Project timeline with parent tasks, subtasks, planned dates, and actual execution.</div>
        </div>
        <div className="pm-gantt-legend">
          <span><i className="bg-success" /> Done</span>
          <span><i className="bg-primary" /> In Progress</span>
          <span><i className="bg-warning" /> In Review</span>
          <span><i className="bg-danger" /> Blocked</span>
          <span><i className="bg-secondary" /> To Do</span>
        </div>
      </div>

      <div className="pm-gantt-project-board">
        <div className="pm-gantt-project-header">
          <div className="pm-gantt-project-label">Task</div>
          <div className="pm-gantt-project-track" style={trackStyle}>
            <div className="pm-gantt-project-months">
              {months.map((m, i) => (
                <div key={i} className="pm-gantt-project-month" style={{ left: `${m.left}%`, width: `${m.width}%` }}>{m.label}</div>
              ))}
            </div>
            <div className="pm-gantt-project-ticks">
              {dateTicks.map((tick, i) => (
                <div key={i} className="pm-gantt-project-tick" style={{ left: `${tick.left}%` }}>{tick.label}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="pm-gantt-project-row pm-gantt-project-row-project">
          <div className="pm-gantt-project-label">
            <div className="pm-gantt-project-row-title">Project</div>
            <div className="fw-bold">{project?.title || 'Untitled project'}</div>
            <div className="pm-gantt-project-dates">
              <span>{fmtDate(project?.projectStartDate)} - {fmtDate(project?.projectEndDate)}</span>
              <span>{projectProgress}% complete</span>
            </div>
          </div>
          <div className="pm-gantt-project-track" style={trackStyle}>
            {projectBar && (
              <div
                className="pm-gantt-project-bar pm-gantt-project-bar-project"
                style={{ left: `${projectBar.left}%`, width: `${projectBar.width}%` }}
                title={`Plan: ${fmtDate(project?.projectStartDate)} - ${fmtDate(project?.projectEndDate)}\nProgress: ${projectProgress}%`}
              >
                <GanttBarLabel bar={projectBar} label={`${projectProgress}%`} />
              </div>
            )}
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="pm-gantt-project-row">
            <div className="pm-gantt-project-label text-muted pm-detail-helper">No tasks yet.</div>
            <div className="pm-gantt-project-track" style={trackStyle} />
          </div>
        ) : rows.map(({ kind, task }) => {
          const planBar = buildBar(timeline, getPlanStart(task), getPlanFinish(task));
          const actualStartVal = getActualStart(task);
          const actualFinishVal = getActualFinish(task);
          const actualBar = actualStartVal ? buildBar(timeline, actualStartVal, actualFinishVal || today) : null;
          const progress = getProgressPct(task);
          const tone = statusTone(taskKey(task));
          const tooltip = [
            `Plan: ${fmtDate(getPlanStart(task))} - ${fmtDate(getPlanFinish(task))}`,
            actualStartVal ? `Actual: ${fmtDate(actualStartVal)} - ${actualFinishVal ? fmtDate(actualFinishVal) : 'ongoing'}` : null,
            `Progress: ${progress}%`,
          ].filter(Boolean).join('\n');

          return (
            <div key={task.id} className={`pm-gantt-project-row pm-gantt-project-row-${kind}`}>
              <div className="pm-gantt-project-label">
                <div className={kind === 'parent' ? 'fw-bold' : 'text-muted'} style={{ fontSize: '0.875rem' }}>{task.title}</div>
                <div className="pm-gantt-project-dates">
                  <span>Plan: {fmtDate(getPlanStart(task))} - {fmtDate(getPlanFinish(task))}</span>
                </div>
              </div>
              <div className="pm-gantt-project-track" style={trackStyle}>
                {planBar && (
                  <div
                    className={`pm-gantt-project-bar pm-gantt-project-plan bg-${tone}`}
                    style={{ left: `${planBar.left}%`, width: `${planBar.width}%` }}
                    title={tooltip}
                  >
                    <GanttBarLabel bar={planBar} label={`${progress}%`} />
                  </div>
                )}
                {actualBar && (
                  <div className="pm-gantt-project-actual" style={{ left: `${actualBar.left}%`, width: `${actualBar.width}%` }} title={tooltip} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Progress Dashboard ───────────────────────────────────────────────────────

function ProgressTab({ tasks }) {
  const [selectedMetric, setSelectedMetric] = useState('variance');
  const dashboard = useMemo(() => {
    const referenceDate = new Date();
    const rows = tasks.map((task) => {
      const plannedPct = getPlannedPct(task, referenceDate);
      const actualPct = getProgressPct(task);
      const variancePct = actualPct - plannedPct;
      const varianceDays = getScheduleVarianceDays(task, referenceDate);
      const parentId = getParentId(task);
      const needsAttention = varianceDays > 0 || variancePct < -15;
      const planStart = parseDate(getPlanStart(task));
      const planFinish = parseDate(getPlanFinish(task));
      const actualStart = parseDate(getActualStart(task));
      const actualFinish = parseDate(getActualFinish(task));
      return {
        ...task,
        plannedPct,
        actualPct,
        variancePct,
        varianceDays,
        needsAttention,
        planStart,
        planFinish,
        actualStart,
        actualFinish,
        typeLabel: parentId ? 'Subtask' : 'Parent',
      };
    });

    const avg = (values) => values.length
      ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
      : 0;
    const delayed = rows.filter((row) => row.varianceDays > 0);
    const behind = rows.filter((row) => row.variancePct < -15);
    const completed = rows.filter((row) => row.actualPct >= 100);
    const parentRows = rows.filter((row) => getParentId(row) === '');
    const subtaskRows = rows.filter((row) => getParentId(row) !== '');
    const timelineDates = rows.flatMap((row) => [
      row.planStart,
      row.planFinish,
      row.actualStart,
      row.actualFinish,
    ]).filter(Boolean);
    const minTimelineDate = timelineDates.length ? new Date(Math.min(...timelineDates.map((date) => date.getTime()))) : null;
    const maxTimelineDate = timelineDates.length ? new Date(Math.max(...timelineDates.map((date) => date.getTime()))) : null;
    if (minTimelineDate) minTimelineDate.setDate(minTimelineDate.getDate() - 1);
    if (maxTimelineDate) maxTimelineDate.setDate(maxTimelineDate.getDate() + 1);
    const timelineDays = minTimelineDate && maxTimelineDate
      ? Math.max(1, Math.ceil((maxTimelineDate - minTimelineDate) / 86400000))
      : 1;
    const toPosition = (date) => {
      if (!date || !minTimelineDate) return null;
      return Math.max(0, Math.min(100, ((date - minTimelineDate) / 86400000 / timelineDays) * 100));
    };
    const toBar = (start, finish) => {
      const left = toPosition(start);
      const right = toPosition(finish || start);
      if (left === null || right === null) return null;
      return {
        left: Math.min(left, right),
        width: Math.max(2, Math.abs(right - left)),
      };
    };
    const timelineRows = rows.map((row) => {
      const actualEnd = row.actualFinish || (row.actualStart && row.actualPct > 0 ? referenceDate : null);
      return {
        ...row,
        planBar: toBar(row.planStart, row.planFinish),
        actualBar: toBar(row.actualStart, actualEnd),
      };
    });
    const tickCount = Math.min(6, Math.max(2, timelineDays + 1));
    const timelineTicks = Array.from({ length: tickCount }, (_, index) => {
      if (!minTimelineDate) return null;
      const tickDate = new Date(minTimelineDate.getTime());
      tickDate.setDate(tickDate.getDate() + Math.round((timelineDays / Math.max(1, tickCount - 1)) * index));
      return {
        date: tickDate,
        label: tickDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        left: (index / Math.max(1, tickCount - 1)) * 100,
      };
    }).filter(Boolean);
    const actualPctAt = (row, date) => {
      if (!row.actualStart || date < row.actualStart) return 0;
      if (row.actualFinish && date >= row.actualFinish) return 100;
      return row.actualPct;
    };
    const trendPoints = timelineTicks.map((tick) => ({
      label: tick.label,
      planned: avg(rows.map((row) => getPlannedPct(row, tick.date))),
      actual: avg(rows.map((row) => actualPctAt(row, tick.date))),
    }));

    return {
      rows,
      timelineRows,
      timelineTicks,
      trendPoints,
      hasTimeline: timelineRows.some((row) => row.planBar || row.actualBar),
      // Averaged over root/parent tasks only, matching the project-level
      // progress rule (see Go backend's projectProgressFromTasks) — a
      // subtask's progress is already folded into its parent's, so
      // including both here would double-count it and disagree with the
      // header's project progress figure.
      plannedAvg: avg(parentRows.map((row) => row.plannedPct)),
      actualAvg: avg(parentRows.map((row) => row.actualPct)),
      delayedCount: delayed.length,
      behindCount: behind.length,
      completedCount: completed.length,
      delayedRows: delayed.sort((a, b) => b.varianceDays - a.varianceDays || a.title.localeCompare(b.title)),
      behindRows: behind.sort((a, b) => a.variancePct - b.variancePct || a.title.localeCompare(b.title)),
      completedRows: completed.sort((a, b) => a.title.localeCompare(b.title)),
      parentAvg: avg(parentRows.map((row) => row.actualPct)),
      subtaskAvg: avg(subtaskRows.map((row) => row.actualPct)),
    };
  }, [tasks]);

  const varianceTone = dashboard.actualAvg >= dashboard.plannedAvg ? 'success' : 'warning';
  const varianceLabel = `${dashboard.actualAvg - dashboard.plannedAvg >= 0 ? '+' : ''}${dashboard.actualAvg - dashboard.plannedAvg}%`;
  const metricViews = {
    planned: {
      title: 'Planned progress tasks',
      description: 'Task list with planned schedule progress as of today.',
      rows: dashboard.rows,
      value: (row) => `${row.plannedPct}% planned`,
      tone: 'primary',
    },
    actual: {
      title: 'Actual progress tasks',
      description: 'Task list with current actual completion progress.',
      rows: dashboard.rows,
      value: (row) => `${row.actualPct}% actual`,
      tone: 'success',
    },
    variance: {
      title: 'Progress variance tasks',
      description: 'Tasks whose actual progress is behind planned progress.',
      rows: dashboard.behindRows,
      value: (row) => `${row.variancePct >= 0 ? '+' : ''}${row.variancePct}% gap`,
      tone: 'warning',
    },
    delayed: {
      title: 'Delayed tasks',
      description: 'Tasks whose schedule is later than planned.',
      rows: dashboard.delayedRows,
      value: (row) => `${row.varianceDays}d late`,
      tone: 'danger',
    },
    completed: {
      title: 'Completed tasks',
      description: 'Tasks with actual progress at 100%.',
      rows: dashboard.completedRows,
      value: (row) => `${row.actualPct}% complete`,
      tone: 'info',
    },
  };
  const selectedView = metricViews[selectedMetric] || metricViews.variance;
  const trendPath = (key) => dashboard.trendPoints
    .map((point, index) => {
      const x = dashboard.trendPoints.length <= 1 ? 0 : (index / (dashboard.trendPoints.length - 1)) * 100;
      const y = 100 - clampPct(point[key]);
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
  const plannedPath = trendPath('planned');
  const actualPath = trendPath('actual');
  const plannedAreaPath = plannedPath ? `${plannedPath} L 100 100 L 0 100 Z` : '';
  const actualAreaPath = actualPath ? `${actualPath} L 100 100 L 0 100 Z` : '';

  if (tasks.length === 0) {
    return <EmptyTabState icon={BarChart3} title="No progress data yet" description="Task plan and actual data will appear here once tasks are created." />;
  }

  return (
    <div className="pm-progress-dashboard">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h3 className="card-title mb-1">Plan vs Actual Dashboard</h3>
          <div className="text-muted pm-detail-helper">Compare planned schedule progress with actual task completion.</div>
        </div>
        <span className="badge bg-green-lt">Live task data</span>
      </div>

      <div className="row g-3 mb-3">
        <SummaryCard icon={BarChart3} tone="primary" value={`${dashboard.plannedAvg}%`} label="Planned progress" onClick={() => setSelectedMetric('planned')} isActive={selectedMetric === 'planned'} />
        <SummaryCard icon={CheckCircle2} tone="success" value={`${dashboard.actualAvg}%`} label="Actual progress" onClick={() => setSelectedMetric('actual')} isActive={selectedMetric === 'actual'} />
        <SummaryCard icon={Activity} tone={varianceTone} value={varianceLabel} label="Progress variance" onClick={() => setSelectedMetric('variance')} isActive={selectedMetric === 'variance'} />
        <SummaryCard icon={Clock} tone="warning" value={dashboard.delayedCount} label="Delayed tasks" onClick={() => setSelectedMetric('delayed')} isActive={selectedMetric === 'delayed'} />
        <SummaryCard icon={ListChecks} tone="info" value={`${dashboard.completedCount}/${tasks.length}`} label="Completed tasks" onClick={() => setSelectedMetric('completed')} isActive={selectedMetric === 'completed'} />
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <div className="card h-100">
            <div className="card-header">
              <div>
                <div className="card-title mb-0">Plan vs Actual Timeline</div>
                <div className="card-subtitle text-muted">Cross-check planned dates against actual execution dates.</div>
              </div>
            </div>
            <div className="card-body">
              {!dashboard.hasTimeline ? (
                <div className="pm-detail-empty-state text-muted" style={{ minHeight: 260 }}>
                  <CalendarDays size={36} className="mb-2 opacity-25" />
                  <div className="fw-semibold">No date range available</div>
                  <div className="pm-detail-helper">Add plan and actual dates to compare schedule movement.</div>
                </div>
              ) : (
                <div className="pm-timeline-compare">
                  <div className="pm-plan-actual-chart mb-4">
                    <div className="pm-plan-actual-chart-head">
                      <div>
                        <div className="fw-semibold">Schedule Progress Trend</div>
                        <div className="text-muted pm-detail-helper">Planned progress compared with actual completion over time.</div>
                      </div>
                      <div className="pm-plan-actual-gap">
                        Gap <strong className={dashboard.actualAvg >= dashboard.plannedAvg ? 'text-success' : 'text-warning'}>{varianceLabel}</strong>
                      </div>
                    </div>
                    <div className="pm-plan-actual-plot">
                      <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Plan versus actual progress chart">
                        <line className="pm-plan-actual-grid" x1="0" y1="0" x2="100" y2="0" />
                        <line className="pm-plan-actual-grid" x1="0" y1="25" x2="100" y2="25" />
                        <line className="pm-plan-actual-grid" x1="0" y1="50" x2="100" y2="50" />
                        <line className="pm-plan-actual-grid" x1="0" y1="75" x2="100" y2="75" />
                        <line className="pm-plan-actual-grid" x1="0" y1="100" x2="100" y2="100" />
                        {plannedAreaPath && <path className="pm-plan-actual-area pm-plan-area" d={plannedAreaPath} />}
                        {actualAreaPath && <path className="pm-plan-actual-area pm-actual-area" d={actualAreaPath} />}
                        {plannedPath && <path className="pm-plan-actual-line pm-plan-line" d={plannedPath} />}
                        {actualPath && <path className="pm-plan-actual-line pm-actual-line" d={actualPath} />}
                      </svg>
                    </div>
                    <div className="pm-plan-actual-xaxis">
                      {dashboard.trendPoints.map((point) => (
                        <span key={point.label}>{point.label}</span>
                      ))}
                    </div>
                  </div>
                  <div className="pm-timeline-legend mb-3">
                    <span><i className="pm-timeline-plan-dot" /> Plan</span>
                    <span><i className="pm-timeline-actual-dot" /> Actual</span>
                    <span><i className="pm-timeline-today-dot" /> Today / in progress</span>
                  </div>
                  <div className="pm-timeline-axis">
                    {dashboard.timelineTicks.map((tick) => (
                      <span key={`${tick.label}-${tick.left}`} style={{ left: `${tick.left}%` }}>{tick.label}</span>
                    ))}
                  </div>
                  <div className="pm-timeline-list">
                    {dashboard.timelineRows.map((row) => (
                      <div className="pm-timeline-row" key={row.id}>
                        <div className="pm-timeline-label">
                          <div className="fw-semibold text-truncate">{row.title}</div>
                          <div className="text-muted pm-detail-helper">{row.typeLabel} / {row.actualPct}%</div>
                        </div>
                        <div className="pm-timeline-track">
                          {dashboard.timelineTicks.map((tick) => (
                            <span className="pm-timeline-gridline" key={`${row.id}-${tick.left}`} style={{ left: `${tick.left}%` }} />
                          ))}
                          {row.planBar && (
                            <span
                              className="pm-timeline-bar pm-timeline-plan"
                              style={{ left: `${row.planBar.left}%`, width: `${row.planBar.width}%` }}
                              title={`Plan: ${fmtDate(row.planStart)} - ${fmtDate(row.planFinish)}`}
                            />
                          )}
                          {row.actualBar && (
                            <span
                              className={`pm-timeline-bar pm-timeline-actual ${row.actualFinish ? '' : 'pm-timeline-open'}`}
                              style={{ left: `${row.actualBar.left}%`, width: `${row.actualBar.width}%` }}
                              title={`Actual: ${fmtDate(row.actualStart)} - ${fmtDate(row.actualFinish)}`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card h-100">
            <div className="card-header">
              <div>
                <div className="card-title mb-0">{selectedView.title}</div>
                <div className="card-subtitle text-muted">{selectedView.description}</div>
              </div>
            </div>
            <div className="card-body">
              {selectedView.rows.length === 0 ? (
                <div className="pm-detail-empty-state text-muted" style={{ minHeight: 220 }}>
                  <CheckCircle2 size={36} className="mb-2 opacity-25" />
                  <div className="fw-semibold">No tasks here</div>
                  <div className="pm-detail-helper">Nothing matches this metric right now.</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter">
                    <thead>
                      <tr>
                        <th>Task</th>
                        <th className="text-end">{selectedView.title}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedView.rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <div className="fw-semibold">{row.title}</div>
                            <div className="text-muted pm-detail-helper">{row.typeLabel} / {statusLabel(row)}</div>
                          </td>
                          <td className="text-end">
                            <span className={`badge bg-${selectedView.tone}-lt`}>{selectedView.value(row)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function ProjectOverviewSummary({ project, users, usersLoading, loadUsers, isDataUnavailable, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    picUserId: '',
    owner: '',
    projectStartDate: '',
    projectEndDate: '',
  });

  useEffect(() => {
    if (!editing) {
      setForm({
        picUserId: project.picUserId ? String(project.picUserId) : '',
        owner: project.owner || '',
        projectStartDate: toInputDate(project.projectStartDate),
        projectEndDate: toInputDate(project.projectEndDate),
      });
    }
  }, [editing, project.owner, project.picUserId, project.projectStartDate, project.projectEndDate]);

  const userOptions = useMemo(() => (users || [])
    .map((user) => {
      const userId = user.id ?? user.user_id ?? user.userId;
      if (!userId) return null;
      return {
        value: String(userId),
        label: user.full_name || user.name || user.username || user.email || `User #${userId}`,
      };
    })
    .filter(Boolean), [users]);

  const summaryItems = [
    { key: 'pic', label: 'PIC', value: project.pic || '-' },
    { key: 'owner', label: 'Owner', value: project.owner || '-' },
    { key: 'projectStartDate', label: 'Project Start Date', value: fmtDate(project.projectStartDate) },
    { key: 'projectEndDate', label: 'Project End Date', value: fmtDate(project.projectEndDate) },
  ];

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const handleSave = async () => {
    if (isDataUnavailable) return;
    const saved = await onSave(form);
    if (saved !== false) setEditing(false);
  };

  return (
    <div className="pm-overview-summary mb-4">
      <div className="pm-overview-scope">
        <div className="page-pretitle mb-2">Project Scope</div>
        <div className="pm-overview-scope-text">
          {isDataUnavailable ? '-' : (project.scope || 'No scope description available.')}
        </div>
      </div>
      <div className="pm-overview-facts">
        <div className="pm-overview-facts-actions">
          {editing ? (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm pm-overview-action-btn" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm pm-overview-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center pm-overview-action-btn"
              onClick={() => {
                setEditing(true);
                loadUsers();
              }}
              disabled={isDataUnavailable}
            >
              <Pencil size={14} className="me-1" />
              Edit
            </button>
          )}
        </div>
        {summaryItems.map((item) => (
          <div className="pm-overview-fact" key={item.label}>
            <span>{item.label}</span>
            {editing && item.key === 'pic' ? (
              <Select
                classNamePrefix="pm-combo-select"
                options={userOptions}
                value={userOptions.find((option) => option.value === form.picUserId) || null}
                onChange={(selected) => set('picUserId', selected?.value || '')}
                isLoading={usersLoading}
                placeholder="Select a user..."
                noOptionsMessage={() => (usersLoading ? 'Loading...' : 'No available users')}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            ) : editing && item.key === 'owner' ? (
              <input className="form-control" value={form.owner} onChange={(e) => set('owner', e.target.value)} />
            ) : editing && item.key === 'projectStartDate' ? (
              <input type="date" className="form-control" value={form.projectStartDate} onChange={(e) => set('projectStartDate', e.target.value)} />
            ) : editing && item.key === 'projectEndDate' ? (
              <input type="date" className="form-control" value={form.projectEndDate} onChange={(e) => set('projectEndDate', e.target.value)} />
            ) : (
              <strong>{isDataUnavailable ? '-' : item.value}</strong>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Team Member Modal (Add / Edit) ───────────────────────────────────────────

function MemberModal({ isOpen, onClose, onSave, member, allUsers, allUsersLoading, existingUserIds, saving }) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [isLeader, setIsLeader] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUserId(member ? String(member.user_id) : '');
      setRole(member ? (member.role || '') : '');
      setIsLeader(member ? Boolean(member.is_leader) : false);
    }
  }, [isOpen, member]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const userOptions = useMemo(() => (allUsers || [])
    .filter((u) => (member ? String(u.id) === String(member.user_id) : !existingUserIds.has(String(u.id))))
    .map((u) => ({ value: String(u.id), label: u.name || u.email || `User #${u.id}` })),
  [allUsers, existingUserIds, member]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!member && !userId) return;
    onSave({
      userId: userId ? Number(userId) : undefined,
      role: role.trim() || null,
      isLeader,
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal modal-blur fade show pm-task-modal" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{member ? 'Edit Team Member' : 'Add Team Member'}</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Member</label>
                  {member ? (
                    <input className="form-control" value={member.name || ''} disabled />
                  ) : (
                    <Select
                      classNamePrefix="pm-combo-select"
                      options={userOptions}
                      value={userOptions.find((o) => o.value === userId) || null}
                      onChange={(selected) => setUserId(selected?.value || '')}
                      isLoading={allUsersLoading}
                      placeholder="Select a user..."
                      noOptionsMessage={() => (allUsersLoading ? 'Loading...' : 'No available users')}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Role</label>
                  <input
                    className="form-control"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g. Auditor, Reviewer, Project Owner"
                  />
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pm-member-leader"
                    checked={isLeader}
                    onChange={(e) => setIsLeader(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pm-member-leader">Project leader</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary ms-auto" disabled={saving || (!member && !userId)}>
                  {saving ? 'Saving...' : 'Save Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
}

// ─── Task Create Modal ────────────────────────────────────────────────────────

function TaskModal({ isOpen, onClose, onSave, tasks, members, initialParentTaskId, saving }) {
  const [form, setForm] = useState({
    title: '', description: '', planStartDate: '', planFinishDate: '',
    actualStartDate: '', actualFinishDate: '', progressPct: '0',
    parentTaskId: '', assigneeIds: [], priorityId: '2', status: 'to_do',
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        title: '',
        description: '',
        planStartDate: '',
        planFinishDate: '',
        actualStartDate: '',
        actualFinishDate: '',
        progressPct: '0',
        parentTaskId: initialParentTaskId ? String(initialParentTaskId) : '',
        assigneeIds: [],
        priorityId: '2',
        status: 'to_do',
      });
    }
  }, [isOpen, initialParentTaskId]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const parentOptions = useMemo(() => {
    const byParent = new Map();
    tasks.forEach((task) => {
      const parentId = getParentId(task);
      const existing = byParent.get(parentId) || [];
      existing.push(task);
      byParent.set(parentId, existing);
    });

    const flatten = (parentId = '', depth = 0) => (byParent.get(parentId) || []).flatMap((task) => [
      { task, depth },
      ...flatten(idKey(task.id), depth + 1),
    ]);

    return flatten();
  }, [tasks]);

  const memberOptions = useMemo(() => (members || [])
    .map((member) => {
      const userId = member.user_id ?? member.userId;
      if (!userId) return null;
      return {
        value: String(userId),
        label: member.name || member.full_name || member.username || member.email || `User #${userId}`,
      };
    })
    .filter(Boolean), [members]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // To Do / Done always lock to 0%/100% on the backend (leafProgressForStatus),
  // ignoring any manual value — same rule the Task Detail drawer's Edit form
  // already respects (see LOCKED_LEAF_PROGRESS above). Mirrored here so the
  // Progress % field doesn't invite typing a number that silently reverts.
  const lockedProgress = LOCKED_LEAF_PROGRESS[form.status] ?? null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave({
      title: form.title.trim(),
      description: form.description || null,
      planStartDate: form.planStartDate || null,
      planFinishDate: form.planFinishDate || null,
      actualStartDate: form.actualStartDate || null,
      actualFinishDate: form.actualFinishDate || null,
      progressPct: lockedProgress !== null ? lockedProgress : clampPct(form.progressPct),
      startDate: form.planStartDate || null,
      deadline: form.planFinishDate || null,
      parentTaskId: form.parentTaskId || null,
      assignedTo: form.assigneeIds[0] ? Number(form.assigneeIds[0]) : 0,
      collaborators: form.assigneeIds.join(','),
      priorityId: Number(form.priorityId),
      status: form.status,
    });
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="modal modal-blur fade show pm-task-modal" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{form.parentTaskId ? 'New Subtask' : 'New Task'}</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <h4 className="card-title mb-0">Task Details</h4>
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Title</label>
                    <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Enter task title" required autoFocus />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Add notes, audit context, or expected output" />
                  </div>
                  {parentOptions.length > 0 && (
                    <div className="col-12">
                      <label className="form-label">Parent Task <span className="text-muted">(optional)</span></label>
                      <select className="form-select" value={form.parentTaskId} onChange={e => set('parentTaskId', e.target.value)}>
                        <option value="">None - create as parent task</option>
                        {parentOptions.map(({ task, depth }) => (
                          <option key={task.id} value={task.id}>
                            {`${'— '.repeat(depth)}${task.title}`}
                          </option>
                        ))}
                      </select>
                      <div className="form-hint">Select a parent to create a child task under it.</div>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label">Assignees</label>
                    <Select
                      classNamePrefix="pm-combo-select"
                      isMulti
                      options={memberOptions}
                      value={memberOptions.filter((option) => form.assigneeIds.includes(option.value))}
                      onChange={(selected) => set('assigneeIds', (selected || []).map((option) => option.value))}
                      placeholder={memberOptions.length ? 'Select project members...' : 'No project members yet'}
                      noOptionsMessage={() => 'No project members available'}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                    <div className="form-hint">Options are taken from members in this project.</div>
                  </div>
                </div>

                <div className="hr-text my-4">Planning</div>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Plan Start</label>
                    <input type="date" className="form-control" value={form.planStartDate} onChange={e => set('planStartDate', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Plan Finish</label>
                    <input type="date" className="form-control" value={form.planFinishDate} onChange={e => set('planFinishDate', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Actual Start</label>
                    <input type="date" className="form-control" value={form.actualStartDate} onChange={e => set('actualStartDate', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Actual Finish</label>
                    <input type="date" className="form-control" value={form.actualFinishDate} onChange={e => set('actualFinishDate', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Progress %</label>
                    {lockedProgress !== null ? (
                      <>
                        <input type="number" className="form-control" value={lockedProgress} disabled />
                        <div className="form-hint">Automatically set to {lockedProgress}% for {KANBAN_COLS.find(c => c.key === form.status)?.label || form.status} status.</div>
                      </>
                    ) : (
                      <input type="number" className="form-control" min="0" max="100" value={form.progressPct} onChange={e => set('progressPct', e.target.value)} />
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={form.priorityId} onChange={e => set('priorityId', e.target.value)}>
                      <option value="1">Low</option>
                      <option value="2">Normal</option>
                      <option value="3">High</option>
                      <option value="4">Critical</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Initial Status</label>
                    <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                      {KANBAN_COLS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary ms-auto" disabled={saving}>{saving ? 'Saving...' : 'Save Task'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
}

// ─── Task Detail Side Panel ───────────────────────────────────────────────────

function TaskDetailPanel({ task, tasks, members, allUsers, onLoadAllUsers, onClose, onAddChild, onDelete, onSelectTask, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const fetchActivity = useCallback(async (taskId) => {
    if (!taskId) return;
    setActivityLoading(true);
    try {
      const res = await withTimeout(api.get(`/v1/pm/tasks/${taskId}/activity`));
      setActivityLogs(Array.isArray(res.data) ? res.data : []);
    } catch {
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchActivity(task?.id);
  }, [task?.id, fetchActivity]);

  // ─── Work Timer (clock in/out) ───────────────────────────────────────────
  const currentUserId = getCurrentUserId();
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeLogsLoading, setTimeLogsLoading] = useState(false);
  const [activeLog, setActiveLog] = useState(null); // my open session on THIS task
  const [elsewhereLog, setElsewhereLog] = useState(null); // my open session on a DIFFERENT task
  const [totalDurationSeconds, setTotalDurationSeconds] = useState(0);
  const [myDurationSeconds, setMyDurationSeconds] = useState(0);
  const [clockActionLoading, setClockActionLoading] = useState(false);
  const [clockError, setClockError] = useState('');
  const [clockOutNote, setClockOutNote] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const fetchTimeLogs = useCallback(async (taskId) => {
    if (!taskId) return;
    setTimeLogsLoading(true);
    try {
      const [logsRes, activeRes] = await Promise.all([
        withTimeout(api.get(`/v1/pm/tasks/${taskId}/time-logs`, { params: currentUserId ? { userId: currentUserId } : {} })),
        currentUserId
          ? withTimeout(api.get('/v1/pm/time-logs/active', { params: { userId: currentUserId } }))
          : Promise.resolve({ data: { active: false } }),
      ]);
      const data = logsRes.data || {};
      setTimeLogs(Array.isArray(data.logs) ? data.logs : []);
      setTotalDurationSeconds(Number(data.totalDurationSeconds) || 0);
      setMyDurationSeconds(Number(data.myDurationSeconds) || 0);
      setActiveLog(data.activeLog || null);
      // { active, taskId, taskTitle, taskType, parentTaskId, parentTaskTitle,
      // projectId, projectTitle, startedAt, elapsedSeconds } — flat shape
      // from GET /time-logs/active (2026-07-22). Only relevant here when it
      // points at a DIFFERENT task than the one this drawer is showing;
      // when it's this same task, `activeLog` above (task-scoped) already
      // covers it.
      const globalActive = activeRes.data || { active: false };
      setElsewhereLog(globalActive.active && idKey(globalActive.taskId) !== idKey(taskId) ? globalActive : null);
    } catch {
      setTimeLogs([]);
      setTotalDurationSeconds(0);
      setMyDurationSeconds(0);
      setActiveLog(null);
      setElsewhereLog(null);
    } finally {
      setTimeLogsLoading(false);
    }
  }, [currentUserId]);
  useEffect(() => {
    fetchTimeLogs(task?.id);
  }, [task?.id, fetchTimeLogs]);

  // Running HH:mm:ss display for an active session — ticks every second
  // only while one exists, cleared as soon as it doesn't.
  useEffect(() => {
    if (!activeLog) {
      setElapsedSeconds(0);
      return undefined;
    }
    const startedAt = getRunningTimerStartedAt(activeLog);
    const fallbackStartedAt = startedAt
      ? null
      : Date.now() - (Math.max(0, Number(activeLog.elapsedSeconds) || 0) * 1000);
    const tick = () => {
      const referenceStartedAt = startedAt || fallbackStartedAt;
      setElapsedSeconds(referenceStartedAt ? Math.max(0, Math.floor((Date.now() - referenceStartedAt) / 1000)) : 0);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeLog]);

  const handleClockIn = async () => {
    if (!task || !currentUserId || clockActionLoading) return;
    setClockActionLoading(true);
    setClockError('');
    try {
      await withTimeout(api.post(`/v1/pm/tasks/${task.id}/clock-in`, { userId: Number(currentUserId), actorUserId: currentUserId }));
      await Promise.all([fetchTimeLogs(task.id), fetchActivity(task.id)]);
    } catch (err) {
      setClockError(getSafeApiErrorMessage(err, 'Unable to clock in.'));
    } finally {
      setClockActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!task || !currentUserId || clockActionLoading) return;
    setClockActionLoading(true);
    setClockError('');
    try {
      await withTimeout(api.post(`/v1/pm/tasks/${task.id}/clock-out`, {
        userId: Number(currentUserId),
        actorUserId: currentUserId,
        note: clockOutNote.trim() || undefined,
      }));
      setClockOutNote('');
      await Promise.all([fetchTimeLogs(task.id), fetchActivity(task.id)]);
    } catch (err) {
      setClockError(getSafeApiErrorMessage(err, 'Unable to clock out.'));
    } finally {
      setClockActionLoading(false);
    }
  };

  // ─── Manual Work Log (forgot to clock in/out) ────────────────────────────
  const [showManualLogModal, setShowManualLogModal] = useState(false);
  const [manualLogForm, setManualLogForm] = useState({ userId: '', date: '', startTime: '', endTime: '', note: '' });
  const [manualLogSaving, setManualLogSaving] = useState(false);
  const [manualLogError, setManualLogError] = useState('');

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape' || !task) return;
      if (showManualLogModal) {
        setShowManualLogModal(false);
        return;
      }
      onClose();
    };
    if (task) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [task, onClose, showManualLogModal]);

  const openManualLogModal = () => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setManualLogForm({
      userId: currentUserId || '',
      date: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      startTime: '',
      endTime: '',
      note: '',
    });
    setManualLogError('');
    setShowManualLogModal(true);
    onLoadAllUsers?.();
  };
  const setManualLogField = (key, value) => setManualLogForm((prev) => ({ ...prev, [key]: value }));

  const handleSaveManualLog = async () => {
    if (!task) return;
    if (!manualLogForm.userId || !manualLogForm.date || !manualLogForm.startTime || !manualLogForm.endTime || !manualLogForm.note.trim()) {
      setManualLogError('User, date, start time, end time, and a note are all required.');
      return;
    }
    setManualLogSaving(true);
    setManualLogError('');
    try {
      await withTimeout(api.post(`/v1/pm/tasks/${task.id}/time-logs/manual`, {
        userId: Number(manualLogForm.userId),
        startedAt: localDateTimeToIso(manualLogForm.date, manualLogForm.startTime),
        endedAt: localDateTimeToIso(manualLogForm.date, manualLogForm.endTime),
        note: manualLogForm.note.trim(),
        actorUserId: currentUserId,
      }));
      setShowManualLogModal(false);
      await Promise.all([fetchTimeLogs(task.id), fetchActivity(task.id)]);
    } catch (err) {
      setManualLogError(getSafeApiErrorMessage(err, 'Unable to save this manual log.'));
    } finally {
      setManualLogSaving(false);
    }
  };

  const handleDeleteTimeLog = async (logId) => {
    if (!task || !window.confirm('Delete this manual work log?')) return;
    try {
      await withTimeout(api.delete(`/v1/pm/time-logs/${logId}`, { params: { actorUserId: currentUserId } }));
      await Promise.all([fetchTimeLogs(task.id), fetchActivity(task.id)]);
    } catch (err) {
      window.alert(getSafeApiErrorMessage(err, 'Unable to delete this log.'));
    }
  };

  const [form, setForm] = useState({
    title: '',
    description: '',
    assigneeIds: [],
    planStartDate: '',
    planFinishDate: '',
    actualStartDate: '',
    actualFinishDate: '',
    progressPct: '0',
    priorityId: '2',
    status: 'to_do',
  });

  // Inline status update (Requirement A) — separate from the Edit-mode
  // form/handleSave below on purpose: this is a single-field, save-on-change
  // control available whether or not the user has clicked Edit, so it must
  // not depend on `editing`/`form` state at all. Reuses the same onSave
  // (-> PUT /tasks/:id) the Edit form uses — sending only `status` in the
  // body is a valid partial update (see UpdateProjectTask backend), so this
  // still gets the existing activity-log diff and leaf-progress rules for
  // free without any new backend work.
  const [statusSaveState, setStatusSaveState] = useState('idle'); // idle | saving | saved | error
  const [pendingStatus, setPendingStatus] = useState(null);
  const [lastAttemptedStatus, setLastAttemptedStatus] = useState(null);
  const statusSaveTimerRef = useRef(null);
  const progressInputRef = useRef(null);
  useEffect(() => () => clearTimeout(statusSaveTimerRef.current), []);
  const saveInlineStatus = async (newStatus) => {
    clearTimeout(statusSaveTimerRef.current);
    setLastAttemptedStatus(newStatus);
    setPendingStatus(newStatus);
    setStatusSaveState('saving');
    const saved = await onSave(task.id, { status: newStatus });
    if (saved !== false) {
      setStatusSaveState('saved');
      setPendingStatus(null);
      statusSaveTimerRef.current = setTimeout(() => setStatusSaveState('idle'), 2000);
    } else {
      setPendingStatus(null);
      setStatusSaveState('error');
    }
  };
  const handleInlineStatusChange = (e) => {
    const newStatus = e.target.value;
    if (!task || newStatus === taskKey(task)) return;
    saveInlineStatus(newStatus);
  };
  const handleRetryInlineStatus = () => {
    if (lastAttemptedStatus) saveInlineStatus(lastAttemptedStatus);
  };

  const handleAddChild = () => {
    const id = task.id;
    onClose();
    onAddChild(id);
  };

  const handleDelete = () => {
    const id = task.id;
    onClose();
    onDelete(id);
  };

  const taskMap = useMemo(() => new Map(tasks.map((item) => [idKey(item.id), item])), [tasks]);
  const childTasks = useMemo(() => tasks.filter((item) => getParentId(item) === idKey(task?.id)), [task, tasks]);

  // Every descendant of the open task, at any depth — these (plus the task
  // itself) must never appear as candidate parents, or saving would create
  // a cycle (a task can't become its own descendant's child).
  const descendantIds = useMemo(() => {
    if (!task) return new Set();
    const ids = new Set();
    const stack = [idKey(task.id)];
    while (stack.length) {
      const current = stack.pop();
      tasks.forEach((item) => {
        const itemId = idKey(item.id);
        if (getParentId(item) === current && !ids.has(itemId)) {
          ids.add(itemId);
          stack.push(itemId);
        }
      });
    }
    return ids;
  }, [task, tasks]);

  // Same shape as TaskModal's parentOptions (flattened, indented by depth)
  // but scoped to exclude the open task and its own descendants.
  const parentOptions = useMemo(() => {
    if (!task) return [];
    const excluded = new Set([idKey(task.id), ...descendantIds]);
    const byParent = new Map();
    tasks.forEach((item) => {
      if (excluded.has(idKey(item.id))) return;
      const parentKey = getParentId(item);
      const existing = byParent.get(parentKey) || [];
      existing.push(item);
      byParent.set(parentKey, existing);
    });
    const flatten = (parentKey = '', depth = 0) => (byParent.get(parentKey) || []).flatMap((item) => [
      { task: item, depth },
      ...flatten(idKey(item.id), depth + 1),
    ]);
    return flatten();
  }, [task, tasks, descendantIds]);

  const memberOptions = useMemo(() => (members || [])
    .map((member) => {
      const userId = member.user_id ?? member.userId;
      if (!userId) return null;
      return {
        value: String(userId),
        label: member.name || member.full_name || member.username || member.email || `User #${userId}`,
      };
    })
    .filter(Boolean), [members]);
  const manualLogUserOptions = useMemo(() => {
    const optionsByID = new Map(memberOptions.map((option) => [option.value, option]));
    const addOption = (userId, label) => {
      const value = idKey(userId);
      if (value && !optionsByID.has(value)) {
        optionsByID.set(value, { value, label: label || `User #${value}` });
      }
    };

    // Older projects can have task assignees before pm_crm_project_members
    // was populated. Those assignees must still be selectable for manual logs.
    taskAssigneeIds(task).forEach((userId) => {
      const label = idKey(task?.assigned_to) === idKey(userId) ? task?.assignee_name : '';
      addOption(userId, label);
    });
    addOption(currentUserId, 'Current user');
    (allUsers || []).forEach((user) => {
      addOption(user.user_id ?? user.id, user.name || user.full_name || user.email);
    });

    return Array.from(optionsByID.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allUsers, currentUserId, memberOptions, task]);
  const breadcrumb = useMemo(() => {
    if (!task) return [];
    const chain = [];
    let current = task;
    const visited = new Set();
    while (current && !visited.has(idKey(current.id))) {
      visited.add(idKey(current.id));
      chain.unshift(current);
      current = taskMap.get(getParentId(current));
    }
    return chain;
  }, [task, taskMap]);

  useEffect(() => {
    if (!task || editing) return;
    setForm({
      title: task.title || '',
      description: task.description || '',
      assigneeIds: taskAssigneeIds(task),
      planStartDate: toInputDate(getPlanStart(task)),
      planFinishDate: toInputDate(getPlanFinish(task)),
      actualStartDate: toInputDate(getActualStart(task)),
      actualFinishDate: toInputDate(getActualFinish(task)),
      progressPct: String(getProgressPct(task)),
      priorityId: String(task.priority_id || 2),
      status: taskKey(task),
      parentTaskId: getParentId(task) || '',
    });
  }, [editing, task]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const handleSave = async () => {
    if (!task || !form.title.trim()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      assignedTo: form.assigneeIds[0] ? Number(form.assigneeIds[0]) : 0,
      collaborators: form.assigneeIds.join(','),
      startDate: form.planStartDate || null,
      deadline: form.planFinishDate || null,
      actualStartDate: form.actualStartDate || null,
      actualFinishDate: form.actualFinishDate || null,
      priorityId: Number(form.priorityId),
      status: form.status,
      parentTaskId: form.parentTaskId || null,
    };
    // Parent task progress is derived from subtasks, and To Do/Done always
    // lock to 0%/100% — never send a manual value for either, the backend
    // ignores it anyway but this keeps intent clear.
    if (!isParentTask && LOCKED_LEAF_PROGRESS[form.status] === undefined) {
      payload.progressPct = clampPct(form.progressPct);
    }
    const saved = await onSave(task.id, payload);
    if (saved !== false) {
      setEditing(false);
      fetchActivity(task.id);
    }
  };

  if (!task) return null;

  const progress = getProgressPct(task);
  const parentId = getParentId(task);
  // Backend is the source of truth (has_active_subtasks); childTasks is a
  // client-side fallback for a stale/older API response that omits it.
  const isParentTask = task.has_active_subtasks ?? childTasks.length > 0;
  // To Do / Done are hard-locked leaf statuses (see leafProgressForStatus on
  // the backend — 0%/100% always win over a manual value), so show them
  // read-only too instead of letting the user type a value that silently
  // reverts on save. Reacts to the live status dropdown while editing, so
  // switching status away from To Do/Done unlocks the field immediately.
  const lockedStatusKey = editing ? form.status : taskKey(task);
  const lockedProgress = LOCKED_LEAF_PROGRESS[lockedStatusKey] ?? null;
  const lockedStatusLabel = KANBAN_COLS.find((c) => c.key === lockedStatusKey)?.label || lockedStatusKey;
  const canEditProgress = !isParentTask && lockedProgress === null;
  const progressHint = isParentTask
    ? 'Progress is calculated from subtasks'
    : lockedProgress !== null
      ? `Progress is locked by ${lockedStatusLabel} status`
      : 'Click to update progress';
  const handleProgressShortcut = () => {
    if (!canEditProgress) return;
    setEditing(true);
    window.setTimeout(() => progressInputRef.current?.focus(), 0);
  };
  const assigneeIds = taskAssigneeIds(task);
  const memberNameById = new Map(memberOptions.map((option) => [option.value, option.label]));
  const assigneeNames = assigneeIds
    .map((userId) => memberNameById.get(userId))
    .filter(Boolean);
  const assigneeLabel = assigneeNames.length ? assigneeNames.join(', ') : (task.assignee_name || 'Unassigned');
  const assigneeInitial = (assigneeNames[0] || task.assignee_name || 'U').trim().charAt(0).toUpperCase();

  return (
    <>
      <div className="pm-task-drawer" role="dialog" aria-modal="true">
        <div className="pm-task-drawer-header">
          <div className="pm-task-drawer-heading">
            <div className="pm-task-drawer-icon"><ListChecks size={18} /></div>
            <div style={{ minWidth: 0 }}>
              <div className="page-pretitle mb-1">Task Detail</div>
              {editing ? (
                <input
                  className="form-control pm-task-title-input"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="Task title"
                />
              ) : (
                <h3 className="pm-task-drawer-title">{task.title}</h3>
              )}
              <div className="pm-task-breadcrumb text-muted">
                {breadcrumb.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {index > 0 && <span>/</span>}
                    <button type="button" onClick={() => onSelectTask(item)}>{item.title}</button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          <button className="btn btn-icon btn-ghost-secondary" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="pm-task-drawer-body">
          <div className="pm-task-drawer-hero">
            <div className="pm-task-status-inline">
              <select
                className={`form-select form-select-sm pm-task-status-select bg-${statusTone(pendingStatus || taskKey(task))}-lt`}
                value={pendingStatus || taskKey(task)}
                onChange={handleInlineStatusChange}
                disabled={statusSaveState === 'saving'}
                aria-label="Task status"
              >
                {KANBAN_COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              {statusSaveState === 'saving' && <small className="text-muted pm-task-status-hint">Saving...</small>}
              {statusSaveState === 'saved' && <small className="text-success pm-task-status-hint">Saved</small>}
              {statusSaveState === 'error' && (
                <button type="button" className="btn btn-link btn-sm text-danger p-0 pm-task-status-hint" onClick={handleRetryInlineStatus}>
                  Failed, retry
                </button>
              )}
              <span className={`badge bg-${prioTone(task.priority_id)}-lt`}>{prioLabel(task.priority_id)}</span>
            </div>
            <div
              className={`pm-task-drawer-progress ${canEditProgress ? 'pm-task-drawer-progress-clickable' : 'pm-task-drawer-progress-readonly'}`}
              onClick={handleProgressShortcut}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && canEditProgress) handleProgressShortcut(); }}
              role={canEditProgress ? 'button' : undefined}
              tabIndex={canEditProgress ? 0 : undefined}
              title={progressHint}
              aria-label={progressHint}
            >
              <div className="d-flex justify-content-between mb-2">
                <span>Progress</span>
                <strong>{progress}%</strong>
              </div>
              <div className="progress" style={{ height: 7 }}>
                <div className="progress-bar bg-primary" style={{ width: `${progress}%` }} />
              </div>
              {canEditProgress && (
                <div className="pm-task-progress-hint">
                  <Pencil size={11} /> Click to update progress
                </div>
              )}
            </div>
          </div>
          <div className="pm-task-timer">
            {elsewhereLog ? (
              <div className="pm-task-timer-locked">
                <Clock size={15} />
                <span>
                  Clocked in on another task{elsewhereLog.taskTitle ? `: ${elsewhereLog.taskTitle}` : ''}
                  {elsewhereLog.taskType === 'subtask' && elsewhereLog.parentTaskTitle ? ` (subtask of ${elsewhereLog.parentTaskTitle})` : ''}
                </span>
              </div>
            ) : activeLog ? (
              <div className="pm-task-timer-active">
                <div className="pm-task-timer-display"><Clock size={15} /> {formatDurationClock(elapsedSeconds)}</div>
                <input
                  type="text"
                  className="form-control form-control-sm pm-task-timer-note"
                  placeholder="Note (optional)"
                  value={clockOutNote}
                  onChange={(e) => setClockOutNote(e.target.value)}
                  maxLength={200}
                />
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleClockOut} disabled={clockActionLoading}>
                  {clockActionLoading ? 'Saving...' : 'Clock Out'}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-sm btn-primary pm-task-clockin-btn" onClick={handleClockIn} disabled={clockActionLoading || !currentUserId}>
                <Clock size={14} className="me-1" /> {clockActionLoading ? 'Starting...' : 'Clock In'}
              </button>
            )}
            {clockError && <div className="text-danger pm-detail-helper pm-task-timer-error">{clockError}</div>}
          </div>

          <div className="pm-task-drawer-section">
            <div className="pm-task-drawer-section-head">
              <div>
                <div className="fw-semibold">Work Logs</div>
                <div className="text-muted pm-detail-helper">
                  Total {formatDurationShort(totalDurationSeconds)} · Mine {formatDurationShort(myDurationSeconds)}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {timeLogsLoading && <span className="text-muted pm-detail-helper">Loading...</span>}
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={openManualLogModal}>
                  <Plus size={14} className="me-1" /> Add Manual Log
                </button>
              </div>
            </div>
            {timeLogs.length === 0 ? (
              <div className="pm-task-drawer-empty">
                {timeLogsLoading ? 'Loading work logs...' : 'No work logged yet. Clock in or add a manual log.'}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-vcenter pm-task-timelog-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                      <th>Source</th>
                      <th>Note</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {timeLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.user_name || `User #${log.user_id}`}</td>
                        <td className="text-muted">{fmtActivityTime(log.started_at)}</td>
                        <td className="text-muted">{log.ended_at ? fmtActivityTime(log.ended_at) : <span className="badge bg-primary-lt">Running</span>}</td>
                        <td>{log.ended_at ? formatDurationShort(log.duration_seconds) : '—'}</td>
                        <td>
                          <span className={`badge ${log.source === 'manual' ? 'bg-purple-lt' : 'bg-teal-lt'}`}>
                            {log.source === 'manual' ? 'Manual' : 'Realtime'}
                          </span>
                        </td>
                        <td className="text-muted">{log.note || '—'}</td>
                        <td className="text-end">
                          {log.source === 'manual' && (
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-ghost-danger"
                              title="Delete manual log"
                              onClick={() => handleDeleteTimeLog(log.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="pm-task-drawer-actions">
            {editing ? (
              <>
                <button type="button" className="btn btn-link link-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.title.trim()}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={14} className="me-1" /> Edit
              </button>
            )}
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddChild}>
              <Plus size={14} className="me-1" /> Subtask
            </button>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
              <Trash2 size={14} className="me-1" /> Delete
            </button>
          </div>

          <div className="pm-task-drawer-section">
            <div className="pm-task-drawer-section-head">
              <div className="fw-semibold">Details</div>
              <span className="text-muted pm-detail-helper">{parentId ? 'Child task' : 'Parent task'}</span>
            </div>
            <div className="pm-task-drawer-grid">
              <div>
                <span>Assignee</span>
                {editing ? (
                  <Select
                    classNamePrefix="pm-combo-select"
                    isMulti
                    options={memberOptions}
                    value={memberOptions.filter((option) => form.assigneeIds.includes(option.value))}
                    onChange={(selected) => set('assigneeIds', (selected || []).map((option) => option.value))}
                    placeholder={memberOptions.length ? 'Select project members...' : 'No project members yet'}
                    noOptionsMessage={() => 'No project members available'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                ) : (
                  <strong className="pm-task-assignee"><span>{assigneeInitial}</span>{assigneeLabel}</strong>
                )}
              </div>
              <div><span>Task Type</span><strong>{(editing ? form.parentTaskId : parentId) ? 'Child task' : 'Parent task'}</strong></div>
              <div>
                <span>Parent Task</span>
                {editing ? (
                  <select className="form-select" value={form.parentTaskId} onChange={(e) => set('parentTaskId', e.target.value)}>
                    <option value="">— None (top-level task) —</option>
                    {parentOptions.map(({ task: opt, depth }) => (
                      <option key={opt.id} value={opt.id}>{`${'— '.repeat(depth)}${opt.title}`}</option>
                    ))}
                  </select>
                ) : (
                  <strong>{parentId ? (taskMap.get(parentId)?.title || 'Unknown task') : '— Top-level task —'}</strong>
                )}
              </div>
              <div><span>Status</span>{editing ? <select className="form-select" value={form.status} onChange={(e) => set('status', e.target.value)}>{KANBAN_COLS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</select> : <strong>{statusLabel(task)}</strong>}</div>
              <div><span>Priority</span>{editing ? <select className="form-select" value={form.priorityId} onChange={(e) => set('priorityId', e.target.value)}><option value="1">Low</option><option value="2">Normal</option><option value="3">High</option><option value="4">Critical</option></select> : <strong>{prioLabel(task.priority_id)}</strong>}</div>
              <div><span>Plan Start</span>{editing ? <input type="date" className="form-control" value={form.planStartDate} onChange={(e) => set('planStartDate', e.target.value)} /> : <strong>{fmtDate(getPlanStart(task))}</strong>}</div>
              <div><span>Plan Finish</span>{editing ? <input type="date" className="form-control" value={form.planFinishDate} onChange={(e) => set('planFinishDate', e.target.value)} /> : <strong>{fmtDate(getPlanFinish(task))}</strong>}</div>
              <div><span>Actual Start</span>{editing ? <input type="date" className="form-control" value={form.actualStartDate} onChange={(e) => set('actualStartDate', e.target.value)} /> : <strong>{fmtDate(getActualStart(task))}</strong>}</div>
              <div><span>Actual Finish</span>{editing ? <input type="date" className="form-control" value={form.actualFinishDate} onChange={(e) => set('actualFinishDate', e.target.value)} /> : <strong>{fmtDate(getActualFinish(task))}</strong>}</div>
              <div>
                <span>Progress %</span>
                {isParentTask ? (
                  <strong>{progress}% <small className="text-muted">Calculated from subtasks</small></strong>
                ) : lockedProgress !== null ? (
                  <strong>{lockedProgress}% <small className="text-muted">Locked ({lockedStatusLabel})</small></strong>
                ) : editing ? (
                  <input ref={progressInputRef} type="number" className="form-control" min="0" max="100" value={form.progressPct} onChange={(e) => set('progressPct', e.target.value)} />
                ) : (
                  <strong>{progress}%</strong>
                )}
              </div>
            </div>
          </div>

          <div className="pm-task-drawer-section">
            <div className="pm-task-drawer-section-head">
              <div className="fw-semibold">Description</div>
            </div>
            {editing ? (
              <textarea className="form-control" rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} />
            ) : (
              <div className={task.description ? 'pm-task-description' : 'pm-task-drawer-empty'}>
                {task.description || 'No description added yet.'}
              </div>
            )}
          </div>

          <div className="pm-task-drawer-section">
            <div className="pm-task-drawer-section-head">
              <div>
                <div className="fw-semibold">Subtasks</div>
                <div className="text-muted pm-detail-helper">{childTasks.length} child task{childTasks.length !== 1 ? 's' : ''}</div>
              </div>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddChild}>
                <Plus size={14} className="me-1" /> Add
              </button>
            </div>
            {childTasks.length === 0 ? (
              <div className="pm-task-drawer-empty">No subtasks yet.</div>
            ) : (
              <div className="pm-task-drawer-subtasks">
                {childTasks.map((child) => (
                  <button type="button" key={child.id} onClick={() => onSelectTask(child)}>
                    <div>
                      <strong>{child.title}</strong>
                      <span>{child.assignee_name || 'Unassigned'} / {fmtDate(getPlanFinish(child))}</span>
                    </div>
                    <span className={`badge bg-${statusTone(taskKey(child))}-lt`}>{statusLabel(child)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pm-task-drawer-section">
            <div className="pm-task-drawer-section-head">
              <div className="fw-semibold">Activity</div>
              {activityLoading && <span className="text-muted pm-detail-helper">Loading...</span>}
            </div>
            {activityLogs.length === 0 ? (
              <div className="pm-task-drawer-empty">
                {activityLoading ? 'Loading activity...' : 'No activity yet. Changes to this task will appear here.'}
              </div>
            ) : (
              <ul className="pm-task-activity-list">
                {activityLogs.map((log) => (
                  <li className="pm-task-activity-item" key={log.id}>
                    <span className={`pm-task-activity-dot pm-detail-${activityTone(log.action)}`}>
                      <ActivityActionIcon action={log.action} />
                    </span>
                    <div className="pm-task-activity-body">
                      <div className="pm-task-activity-desc">{log.description}</div>
                      <div className="pm-task-activity-time text-muted">{fmtActivityTime(log.created_at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="pm-task-drawer-backdrop" onClick={onClose} />

      {showManualLogModal && (
        <>
          <div className="modal modal-blur fade show pm-task-modal pm-manual-log-modal" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Manual Log</h5>
                  <button type="button" className="btn-close" onClick={() => setShowManualLogModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label required">User</label>
                      <select className="form-select" value={manualLogForm.userId} onChange={(e) => setManualLogField('userId', e.target.value)}>
                        <option value="">Select a user...</option>
                        {manualLogUserOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label required">Date</label>
                      <input type="date" className="form-control" value={manualLogForm.date} onChange={(e) => setManualLogField('date', e.target.value)} />
                    </div>
                    <div className="col-6">
                      <label className="form-label required">Start Time</label>
                      <input type="time" className="form-control" value={manualLogForm.startTime} onChange={(e) => setManualLogField('startTime', e.target.value)} />
                    </div>
                    <div className="col-6">
                      <label className="form-label required">End Time</label>
                      <input type="time" className="form-control" value={manualLogForm.endTime} onChange={(e) => setManualLogField('endTime', e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label required">Note / Reason</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="e.g. Forgot to clock out"
                        value={manualLogForm.note}
                        onChange={(e) => setManualLogField('note', e.target.value)}
                      />
                    </div>
                    {manualLogError && <div className="col-12"><div className="text-danger pm-detail-helper">{manualLogError}</div></div>}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={() => setShowManualLogModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary ms-auto" onClick={handleSaveManualLog} disabled={manualLogSaving}>
                    {manualLogSaving ? 'Saving...' : 'Save Log'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show pm-manual-log-backdrop" onClick={() => setShowManualLogModal(false)} />
        </>
      )}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const PmProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState(sampleDetail);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isForbidden, setIsForbidden] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskModalParentId, setTaskModalParentId] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [savingOverview, setSavingOverview] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activityFilter, setActivityFilter] = useState('All');
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [savingMember, setSavingMember] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  useEffect(() => {
    fetchProjectDetail();
    fetchTasks();
  }, [id]); // eslint-disable-line

  const fetchProjectDetail = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setIsForbidden(false);
    try {
      const res = await withTimeout(api.get(`/v1/pm/crm-projects/${id}`));
      setProject(normalizeProjectDetail(res.data || {}, id));
    } catch (err) {
      // A 403 here means "you're not a member of this project" — a
      // permanent access restriction, not a transient failure, so it gets
      // its own message and the Retry affordance is suppressed for it.
      if (err?.response?.status === 403) {
        setIsForbidden(true);
        setErrorMessage('You are not a member of this project. Contact your project lead to be added.');
      } else {
        setErrorMessage(getSafeApiErrorMessage(err, 'Unable to load project detail.'));
      }
      setProject(shouldUseSampleData ? { ...sampleDetail, id } : { ...emptyProjectDetail, id });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTasks = async () => {
    setTasksLoading(true);
    if (USE_DUMMY_TASK_DATA) {
      setTasks((sampleDetail.tasks || []).map(task => ({ ...task, project_id: id })));
      setTasksLoading(false);
      return;
    }
    try {
      const res = await withTimeout(api.get(`/v1/pm/crm-projects/${id}/tasks`));
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchMembers = useCallback(async () => {
    try {
      const res = await withTimeout(api.get(`/v1/pm/crm-projects/${id}/members`));
      setProject(prev => ({ ...prev, team: Array.isArray(res.data) ? res.data : [] }));
    } catch { /* keep existing team on failure */ }
  }, [id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSaveOverview = async (formData) => {
    setSavingOverview(true);
    try {
      const res = await api.put(`/v1/pm/crm-projects/${id}/overview`, formData);
      setProject(normalizeProjectDetail(res.data || {}, id));
      await fetchMembers();
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 404
        ? 'Endpoint update overview belum tersedia di backend yang sedang berjalan. Restart/deploy backend, lalu coba Save lagi.'
        : getSafeApiErrorMessage(err, 'Unable to save project overview.');
      window.alert(message);
      return false;
    } finally {
      setSavingOverview(false);
    }
  };

  const handleCreateTask = async (formData) => {
    setSavingTask(true);
    if (USE_DUMMY_TASK_DATA) {
      const nextId = Math.max(0, ...tasks.map(task => Number(task.id) || 0)) + 1;
      const parentTaskId = Number(formData.parentTaskId || 0) || 0;
      const newTask = {
        id: nextId,
        project_id: id,
        title: formData.title,
        description: formData.description,
        parent_task_id: parentTaskId,
        parentTaskId,
        priority_id: Number(formData.priorityId) || 2,
        status: formData.status || 'to_do',
        status_key: formData.status || 'to_do',
        status_title: KANBAN_COLS.find(col => col.key === formData.status)?.label || 'To Do',
        assigned_to: formData.assignedTo,
        collaborators: formData.collaborators,
        assignee_name: project.team.find(member => String(member.user_id ?? member.userId) === String(formData.assignedTo))?.name || 'Unassigned',
        plan_start_date: formData.planStartDate,
        planStartDate: formData.planStartDate,
        plan_finish_date: formData.planFinishDate,
        planFinishDate: formData.planFinishDate,
        actual_start_date: formData.actualStartDate,
        actualStartDate: formData.actualStartDate,
        actual_finish_date: formData.actualFinishDate,
        actualFinishDate: formData.actualFinishDate,
        start_date: formData.planStartDate,
        deadline: formData.planFinishDate,
        progress_pct: formData.progressPct,
        progressPct: formData.progressPct,
      };
      setTasks(prev => [...prev, newTask]);
      closeTaskModal();
      setSavingTask(false);
      return;
    }
    try {
      await api.post(`/v1/pm/crm-projects/${id}/tasks`, { ...formData, actorUserId: getCurrentUserId() });
      closeTaskModal();
      fetchTasks();
      // A new (sub)task changes its parent's and the project's progress —
      // refresh the header too, not just the task list.
      fetchProjectDetail();
    } catch { /* keep modal open */ } finally {
      setSavingTask(false);
    }
  };

  const handleUpdateTask = async (taskId, formData) => {
    setSavingTask(true);
    try {
      const res = await api.put(`/v1/pm/tasks/${taskId}`, { ...formData, actorUserId: getCurrentUserId() });
      const updatedTask = res.data;
      setTasks(prev => prev.map(task => (idKey(task.id) === idKey(taskId) ? { ...task, ...updatedTask } : task)));
      setSelectedTask(prev => (prev && idKey(prev.id) === idKey(taskId) ? { ...prev, ...updatedTask } : prev));
      await fetchTasks();
      // Editing a task (status, progress, or moving it under a different
      // parent) can change parent/project progress — keep the header in sync.
      fetchProjectDetail();
      return true;
    } catch (err) {
      window.alert(getSafeApiErrorMessage(err, 'Unable to update task detail.'));
      return false;
    } finally {
      setSavingTask(false);
    }
  };

  const handleReorderTasks = useCallback(async (_parentId, orderedIds) => {
    const sortEntries = orderedIds.map((taskId, index) => [idKey(taskId), (index + 1) * 1000]);
    const sortMap = new Map(sortEntries);
    const previousTasks = tasks;

    setTasks(prev => prev.map(task => {
      const nextSortOrder = sortMap.get(idKey(task.id));
      return nextSortOrder
        ? { ...task, sort_order: nextSortOrder, sortOrder: nextSortOrder }
        : task;
    }));

    try {
      await Promise.all(sortEntries.map(([taskId, sortOrder]) => (
        api.put(`/v1/pm/tasks/${taskId}`, {
          sortOrder,
          actorUserId: getCurrentUserId(),
        })
      )));
      await fetchTasks();
    } catch (err) {
      setTasks(previousTasks);
      window.alert(getSafeApiErrorMessage(err, 'Unable to reorder tasks.'));
    }
  }, [fetchTasks, tasks]);

  const handleDeleteTask = useCallback(async (taskId) => {
    const collectChildIds = (parentId, allTasks, result = new Set()) => {
      const parentKey = idKey(parentId);
      allTasks.forEach((task) => {
        if (getParentId(task) === parentKey) {
          result.add(idKey(task.id));
          collectChildIds(task.id, allTasks, result);
        }
      });
      return result;
    };
    const childIds = collectChildIds(taskId, tasks);
    const childCount = childIds.size;
    const message = childCount > 0
      ? `Delete this task and ${childCount} subtask${childCount !== 1 ? 's' : ''}?`
      : 'Delete this task?';
    if (!window.confirm(message)) return;
    if (USE_DUMMY_TASK_DATA) {
      const idsToDelete = new Set([idKey(taskId), ...childIds]);
      setTasks(prev => prev.filter(t => !idsToDelete.has(idKey(t.id))));
      setSelectedTask(prev => (prev && idsToDelete.has(idKey(prev.id)) ? null : prev));
      return;
    }
    try {
      await api.delete(`/v1/pm/tasks/${taskId}`, { params: { actorUserId: getCurrentUserId() } });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      // Deleting a (sub)task changes its parent's and the project's progress.
      fetchProjectDetail();
    } catch { /* silent */ }
  }, [tasks]);

  const handleStatusChange = useCallback(async (taskId, newKey) => {
    const statusTitle = KANBAN_COLS.find(col => col.key === newKey)?.label || newKey;
    setTasks(prev => prev.map(t => idKey(t.id) === idKey(taskId) ? { ...t, status: newKey, status_key: newKey, status_title: statusTitle } : t));
    if (USE_DUMMY_TASK_DATA) return;
    try {
      const res = await api.patch(`/v1/pm/tasks/${taskId}/move`, { statusKey: newKey, actorUserId: getCurrentUserId() });
      // The move can change this task's own progress (see the backend's
      // leaf status rules) and, if it has a parent, that parent's + the
      // project's progress too — sync this task from the response right
      // away, then refresh the list/header for anything else affected.
      const updatedTask = res.data;
      setTasks(prev => prev.map(t => (idKey(t.id) === idKey(taskId) ? { ...t, ...updatedTask } : t)));
      fetchTasks();
      fetchProjectDetail();
    } catch {
      fetchTasks();
    }
  }, []); // eslint-disable-line

  const existingMemberUserIds = useMemo(
    () => new Set((project.team || []).map((m) => String(m.user_id))),
    [project.team]
  );

  const fetchAllUsers = useCallback(async () => {
    if (allUsers.length === 0) {
      setAllUsersLoading(true);
      try {
        const res = await withTimeout(api.get('/v1/pm/gantt/members'));
        setAllUsers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setAllUsers([]);
      } finally {
        setAllUsersLoading(false);
      }
    }
  }, [allUsers.length]);

  const openMemberModal = useCallback(async (member = null) => {
    setEditingMember(member);
    setShowMemberModal(true);
    if (!member) await fetchAllUsers();
  }, [fetchAllUsers]);

  const closeMemberModal = useCallback(() => {
    setShowMemberModal(false);
    setEditingMember(null);
  }, []);

  const handleSaveMember = async (formData) => {
    setSavingMember(true);
    try {
      if (editingMember) {
        await api.put(`/v1/pm/crm-projects/${id}/members/${editingMember.id}`, formData);
      } else {
        await api.post(`/v1/pm/crm-projects/${id}/members`, formData);
      }
      closeMemberModal();
      await fetchMembers();
    } catch { /* keep modal open */ } finally {
      setSavingMember(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.name || 'this member'} from the project team?`)) return;
    try {
      await api.delete(`/v1/pm/crm-projects/${id}/members/${member.id}`);
      await fetchMembers();
    } catch { /* silent */ }
  };

  const openTaskModal = useCallback((parentId = '') => {
    setTaskModalParentId(parentId ? String(parentId) : '');
    setShowTaskModal(true);
  }, []);

  const openRootTaskModal = useCallback(() => {
    setActiveTab('tasks');
    openTaskModal();
  }, [openTaskModal]);

  const closeTaskModal = useCallback(() => {
    setShowTaskModal(false);
    setTaskModalParentId('');
  }, []);

  useEffect(() => {
    if (!selectedTask) return;
    const latest = tasks.find(task => idKey(task.id) === idKey(selectedTask.id));
    if (latest && latest !== selectedTask) setSelectedTask(latest);
  }, [selectedTask, tasks]);

  const taskStats = useMemo(() => ({
    total: tasks.length,
    done: tasks.filter(t => taskKey(t) === 'done').length,
    open: tasks.filter(t => taskKey(t) !== 'done').length,
  }), [tasks]);

  const isDataUnavailable = Boolean(errorMessage) && project.title === emptyProjectDetail.title;

  const activityTypes = useMemo(() =>
    ['All', ...Array.from(new Set(project.activity.map(a => a.type || 'Update')))],
    [project.activity]
  );

  const filteredActivity = useMemo(() =>
    project.activity.filter(a => activityFilter === 'All' || (a.type || 'Update') === activityFilter),
    [activityFilter, project.activity]
  );

  const tabLoading = (
    <div className="text-center text-muted py-5">
      <div className="spinner-border spinner-border-sm me-2" />Loading…
    </div>
  );

  return (
    <div className="pm-detail-page">
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/pm/projects')}>
              <ArrowLeft size={16} className="me-1" />Back to Projects
            </button>
            <div className="page-pretitle">Project Management</div>
            <h2 className="page-title">
              <KanbanSquare size={22} className="me-2 text-muted" />
              {isDataUnavailable ? 'Project Detail' : project.title}
            </h2>
            <div className="text-muted mt-1">
              {isDataUnavailable
                ? 'Project detail could not be loaded.'
                : 'Tasks, Kanban board, Gantt timeline, and team workload.'}
            </div>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
                onClick={openRootTaskModal}
                disabled={isDataUnavailable}
              >
                <Plus size={16} />
                Task
              </button>
              <button
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={() => { fetchProjectDetail(); fetchTasks(); }}
                disabled={isLoading}
              >
                <RefreshCw size={16} className={`me-2 ${isLoading ? 'pm-detail-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        {isLoading ? (
          <DetailSkeleton />
        ) : (
          <>
            {errorMessage && (
              <div className="alert alert-warning pm-detail-error-banner d-flex align-items-center mb-3">
                <span className="pm-detail-icon pm-detail-warning me-3"><AlertTriangle size={20} /></span>
                <div className="flex-fill">
                  <div className="fw-semibold">{isForbidden ? 'Access restricted' : 'Project detail unavailable'}</div>
                  <div className="pm-detail-helper">{errorMessage}</div>
                </div>
                {!isForbidden && (
                  <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchProjectDetail}>Retry</button>
                )}
              </div>
            )}

            {/* Project header */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-3 align-items-center">
                  <div className="col-12 col-lg">
                    <div className="text-muted pm-detail-helper">Client / Initiative</div>
                    <div className="fw-semibold">{isDataUnavailable ? '-' : project.clientName}</div>
                  </div>
                  <div className="col-6 col-lg-auto">
                    <div className="text-muted pm-detail-helper">Stage</div>
                    <span className={`badge bg-${isDataUnavailable ? 'secondary' : getStageTone(project.stage)}-lt`}>
                      {isDataUnavailable ? 'Unavailable' : project.stage}
                    </span>
                  </div>
                  <div className="col-6 col-lg-auto">
                    <div className="text-muted pm-detail-helper">Health</div>
                    <span className={`badge bg-${isDataUnavailable ? 'secondary' : getHealthTone(project.health)}-lt`}>
                      {isDataUnavailable ? '-' : project.health}
                    </span>
                  </div>
                  <div className="col-12 col-lg-3">
                    <div className="d-flex justify-content-between pm-detail-helper mb-1">
                      <span>Progress</span>
                      <span className="fw-semibold">{isDataUnavailable ? '-' : `${project.progress}%`}</span>
                    </div>
                    <div className="progress pm-detail-progress">
                      <div className="progress-bar bg-primary" style={{ width: `${isDataUnavailable ? 0 : project.progress}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI summary */}
            <div className="row g-3 mb-3">
              <SummaryCard icon={ListChecks} tone="primary" value={taskStats.total} label="Total tasks" isUnavailable={tasksLoading} />
              <SummaryCard icon={CheckCircle2} tone="success" value={taskStats.done} label="Completed" isUnavailable={tasksLoading} />
              <SummaryCard icon={Clock} tone="warning" value={taskStats.open} label="Open tasks" isUnavailable={tasksLoading} />
              <SummaryCard icon={Users} tone="info" value={project.team.length} label="Team members" isUnavailable={isDataUnavailable} />
              <SummaryCard icon={CalendarDays} tone="purple" value={fmtDate(project.projectEndDate)} label="Deadline" isUnavailable={isDataUnavailable} />
            </div>

            {/* Tab panel */}
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs">
                  {tabs.map(({ key, label, icon: Icon }) => (
                    <li className="nav-item" key={key}>
                      <button className={`nav-link ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
                        <Icon size={15} className="me-1" />{label}
                        {key === 'tasks' && tasks.length > 0 && (
                          <span className="badge bg-primary-lt ms-1" style={{ fontSize: '0.7rem' }}>{tasks.length}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card-body">

                {activeTab === 'overview' && (
                  <>
                    <ProjectOverviewSummary
                      project={project}
                      users={allUsers}
                      usersLoading={allUsersLoading}
                      loadUsers={fetchAllUsers}
                      isDataUnavailable={isDataUnavailable}
                      onSave={handleSaveOverview}
                      saving={savingOverview}
                    />
                    {tasksLoading ? tabLoading : <ProgressTab tasks={tasks} />}
                  </>
                )}

                {activeTab === 'tasks' && (tasksLoading ? tabLoading : (
                  <TasksTab
                    tasks={tasks}
                    onAddTask={openTaskModal}
                    onDeleteTask={handleDeleteTask}
                    onTaskClick={setSelectedTask}
                    onReorderTasks={handleReorderTasks}
                  />
                ))}

                {activeTab === 'kanban' && (tasksLoading ? tabLoading : (
                  <KanbanTab
                    tasks={tasks}
                    onStatusChange={handleStatusChange}
                    onAddTask={openTaskModal}
                    onCardClick={setSelectedTask}
                  />
                ))}

                {activeTab === 'gantt' && (tasksLoading ? tabLoading : <GanttTab project={project} tasks={tasks} />)}

                {activeTab === 'team' && (
                  <div>
                    <div className="pm-team-toolbar">
                      <div>
                        <div className="fw-semibold">Project Team</div>
                        <div className="text-muted pm-detail-helper">Add or remove members and manage their role on this project.</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm d-inline-flex align-items-center pm-team-add-btn"
                        onClick={() => openMemberModal()}
                      >
                        <UserPlus size={15} className="me-1" />
                        Add Member
                      </button>
                    </div>
                    {project.team.length === 0 ? (
                      <EmptyTabState icon={Users} title="No team members yet" description="Add a member to start assigning tasks and tracking workload." />
                    ) : (
                      <div className="row g-3">
                        {project.team.map(member => (
                          <div className="col-12 col-xl-4" key={member.id}>
                            <div className="pm-team-card d-flex align-items-center gap-3">
                              <span className="avatar avatar-md rounded-circle">{member.name?.slice(0, 1)}</span>
                              <div className="flex-fill" style={{ minWidth: 0 }}>
                                <div className="fw-semibold d-flex align-items-center gap-2">
                                  <span className="text-truncate">{member.name}</span>
                                  {member.is_leader && <span className="badge bg-purple-lt flex-shrink-0">Leader</span>}
                                </div>
                                <div className="text-muted pm-detail-helper text-truncate">{member.role || 'No role set'}</div>
                              </div>
                              <span className="badge bg-secondary-lt flex-shrink-0">{member.open_tasks || 0} tasks</span>
                              <div className="btn-list flex-shrink-0">
                                <button type="button" className="btn btn-icon btn-sm btn-ghost-secondary" onClick={() => openMemberModal(member)} title="Edit member">
                                  <Pencil size={14} />
                                </button>
                                <button type="button" className="btn btn-icon btn-sm btn-ghost-danger" onClick={() => handleRemoveMember(member)} title="Remove member">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'activity' && (
                  project.activity.length === 0
                    ? <EmptyTabState icon={Activity} title="No activity yet" description="Activity will appear as tasks and updates happen." />
                    : (
                      <>
                        <div className="pm-activity-toolbar">
                          <div>
                            <div className="fw-semibold">Project Activity</div>
                            <div className="text-muted pm-detail-helper">Task movements, assignments, and updates.</div>
                          </div>
                          <div className="btn-list">
                            {activityTypes.map(type => (
                              <button
                                key={type}
                                type="button"
                                className={`btn btn-sm ${activityFilter === type ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setActivityFilter(type)}
                              >
                                {type}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="pm-activity-timeline">
                          {filteredActivity.map(item => (
                            <div className="pm-activity-item" key={item.id}>
                              <div className="pm-activity-marker">
                                <span className="pm-detail-activity-icon"><Activity size={16} /></span>
                              </div>
                              <div className="pm-activity-content">
                                <div className="d-flex align-items-start justify-content-between gap-3">
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <span className="fw-semibold">{item.title}</span>
                                      <span className="badge bg-primary-lt">{item.type || 'Update'}</span>
                                    </div>
                                    <div className="text-muted pm-detail-helper">{item.description}</div>
                                  </div>
                                  <div className="text-end text-muted pm-detail-helper">
                                    <div>{item.time}</div>
                                    <div>{item.actor || 'System'}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )
                )}

              </div>
            </div>
          </>
        )}
      </div>

      <TaskModal
        isOpen={showTaskModal}
        onClose={closeTaskModal}
        onSave={handleCreateTask}
        tasks={tasks}
        members={project.team}
        initialParentTaskId={taskModalParentId}
        saving={savingTask}
      />
      <TaskDetailPanel
        task={selectedTask}
        tasks={tasks}
        members={project.team}
        allUsers={allUsers}
        onLoadAllUsers={fetchAllUsers}
        onClose={() => setSelectedTask(null)}
        onAddChild={openTaskModal}
        onDelete={handleDeleteTask}
        onSelectTask={setSelectedTask}
        onSave={handleUpdateTask}
        saving={savingTask}
      />
      <MemberModal
        isOpen={showMemberModal}
        onClose={closeMemberModal}
        onSave={handleSaveMember}
        member={editingMember}
        allUsers={allUsers}
        allUsersLoading={allUsersLoading}
        existingUserIds={existingMemberUserIds}
        saving={savingMember}
      />
    </div>
  );
};

export default PmProjectDetail;
